import React, { useEffect, useMemo, useState } from 'react';
import type { AdminAd, AdminUser, AdminActivity, Lead as PanelLead, AdminSubscriptionPlan } from '../../types/admin-panel';

// Helper: obtain stable string id for entities that may have optional id/_id.
function getId(obj: { id?: string; _id?: string } | undefined): string {
  return String(obj?.id || obj?._id || '');
}
import { BarChart3, Users, Eye, Target, Image, Plus, Calendar, Clock, EyeOff, Share2, Phone, Mail } from 'lucide-react';
import safeToast from '../../utils/safeToast';
import CreateCardUserModal from '../CreateCardUserModal';
import EditPlanModal from './EditPlanModal';
import EditAdModal from './EditAdModal';
import EditTemplateModal from './EditTemplateModal';
import EmailTemplatesPanel from './EmailTemplatesPanel';
import { listTemplates as listServerTemplates, createTemplate as createServerTemplate, uploadTCard as uploadServerTCard, uploadTemplateBundle as uploadServerBundle, previewTCard as previewServerTCard, submitTemplateForApproval, type TemplateItem as ServerTemplateItem } from '../../api/templates';
import { TemplatePreviewDialog } from '../templates/TemplatePreviewDialog';
import { PreviewCard, resolveVariant } from '../templates/PreviewCard';
import { PreviewCard as MetaPreviewCard } from '../templates/MetaPreviewCard';
import { ensureTemplateVariant } from '../../lib/templates/normalize';
import { defaultHomepageSettings, mockUsers, mockAds, mockLeads, mockSystemStats } from '../../data/mockData';
import { getUser, updateUser } from '../../api/users';
import { fetchJson } from '../../api/apiClient';
import { listAdminAds } from '../../api/ads';
import { CardCustomizationPage } from '../CardCustomizationPage';
import { getCurrentUser } from '../../api/auth';
import { AdvancedAnalyticsPanel } from '../AdvancedAnalyticsPanel';
import { fetchCohort, type CohortKind } from '../../api/leads';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { resolveImageSrc, DEFAULT_AVATAR, avatarFallbackUrl } from '../../utils/media';
import { getSubscriptionSummary, listExpiredSubscriptions, listSubscriptionPlans } from '../../api/subscriptions';
import { createBulkRequest } from '../../api/bulkRequests';
import { canonicalCardUrl } from '../../utils/url';

// Persisted keys used across the panel (restored after props refactor)
const LOCAL_PENDING_KEY = 'basic_admin_pending';
const SUPERADMIN_QUEUE_KEY = 'superadmin_review_queue';
const ACTIVITY_LOG_KEY = 'basic_admin_activity';
const NOTIFICATIONS_KEY = 'basic_admin_notifications';

type Lead = PanelLead;

// NEW: selection props interface (kept optional to avoid breaking callers)
export interface BasicAdminPanelV2Props {
  selectedUsers?: string[];
  onUserSelect?: (userId: string) => void;
  onSelectedUserChange?: (userId: string) => void;
  // Prefer real-time aggregated stats from server when provided
  systemStats?: any;
  liveMode?: 'sse'|'poll'|'offline';
}

export default function BasicAdminPanelV2(props: BasicAdminPanelV2Props): JSX.Element {
  console.log('[live] Basic admin rendered');
  const { selectedUsers: providedSelectedUsers, onUserSelect, onSelectedUserChange, systemStats: serverSystemStats, liveMode } = props;
  const allowMockStats = !!(serverSystemStats as any)?.analyticsFlags?.enableMockStats;

  const DISABLE_MOCK_USERS = (import.meta as any)?.env?.VITE_DISABLE_MOCK_USERS === '1';
  const CLEAR_USER_CACHE_ON_BOOT = (import.meta as any)?.env?.VITE_CLEAR_USER_CACHE_ON_BOOT === '1';
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [tab, setTab] = useState<'overview'|'sms'|'email'|'users'|'ads'|'subscriptions'|'templates'|'email_templates'|'stats'|'activity'>('overview');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [cohortsLoaded, setCohortsLoaded] = useState<{ sms?: boolean; email?: boolean; incomplete?: boolean }>({});
  // Optional preset for Bulk SMS form (e.g., expired users quick action)
  const [smsPreset, setSmsPreset] = useState<{ recipients?: string[]; title?: string; message?: string; group?: 'all'|'users'|'non_users' } | null>(null);
  // Optional preset for Bulk Email form (mirrors SMS preset shape where applicable)
  const [emailPreset, setEmailPreset] = useState<{ recipients?: string[]; subject?: string; message?: string; group?: 'all'|'users'|'non_users' } | null>(null);
  const [superAdminEmails, setSuperAdminEmails] = useState<string[]>([]);
  const [managersLoaded, setManagersLoaded] = useState(false);
  const [users, setUsers] = useState<AdminUser[]>(() => {
    try {
      const raw = localStorage.getItem('users');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch {}
    return DISABLE_MOCK_USERS ? [] : [...(mockUsers as any[])];
  });
  // Persist users on every change (ensures changes survive reloads)
  useEffect(() => {
    try { localStorage.setItem('users', JSON.stringify(users)); } catch {}
  }, [users]);

  // Runtime marker: expose which admin panel rendered (survives minification)
  useEffect(() => {
    try { (window as any).__tcardAdminPanel = 'basic-v2'; } catch {}
    try {
      // Optional one-time debug toast for superadmins only when flag is enabled
      const badge = (typeof window !== 'undefined' ? localStorage.getItem('admin_debug_badge') : null) || 'show';
      if (badge === 'show') {
        // If we already know currentUser, gate by role; else fall back to a light info
        const uRaw = (typeof window !== 'undefined' ? localStorage.getItem('CURRENT_USER') : null);
        const u = uRaw ? JSON.parse(uRaw) : currentUser;
        const role = String(u?.role || u?.userRole || '').toLowerCase();
        const isSuper = role.includes('super');
        if (isSuper) safeToast('info', 'Admin panel: Basic V2');
      }
    } catch { /* noop */ }
    // No deps: we only need to run once per mount
     
  }, []);

  // Ensure the toast also appears once currentUser becomes available (mount-time may be too early)
  const [__debugToastShown, set__debugToastShown] = useState(false);
  useEffect(() => {
    if (__debugToastShown) return;
    try {
      const badge = (typeof window !== 'undefined' ? localStorage.getItem('admin_debug_badge') : null) || 'show';
      if (badge !== 'show') return;
      const u = currentUser;
      if (!u) return; // wait until user loads
      const role = String(u?.role || u?.userRole || '').toLowerCase();
      const isSuper = role.includes('super') || (u as any)?.isSuperAdmin === true || (u as any)?.superadmin === true;
      if (isSuper) {
        safeToast('info', 'Admin panel: Basic V2');
        set__debugToastShown(true);
      }
    } catch { /* noop */ }
  }, [currentUser, __debugToastShown]);
  useEffect(() => {
    if (CLEAR_USER_CACHE_ON_BOOT) {
      try { localStorage.removeItem('users'); } catch {}
      try { localStorage.removeItem('dev_users'); } catch {}
      try { localStorage.removeItem('dev_users_last_created'); } catch {}
    }
  }, []);
  // On mount, try to load the owner_session user from server and seed users list
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = localStorage.getItem('owner_session');
        if (!raw) return;
        const sess = JSON.parse(raw) as { id?: string|null; username?: string|null };
        const key = (sess?.id || sess?.username);
        if (!key) return;
        const u = await getUser(String(key));
        if (!cancelled && u) {
          setUsers(prev => {
            const exists = prev.some(p => (p.id && (p.id===u.id || p.id===u._id)) || p.username===u.username);
            const next = exists ? prev.map(p => (p.username===u.username || p.id===u.id || p._id===u.id) ? { ...p, ...u } : p) : [u, ...prev];
            try { localStorage.setItem('users', JSON.stringify(next)); } catch {}
            return next;
          });
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // On mount, if admin token exists, load real users from API (overrides mocks/cache)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('dev_admin_token');
        if (!token) return;
        const res = await fetch('/api/admin/users?limit=200', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data?.items)) {
          const normalized = data.items.map((u: any, idx: number) => ({
            ...u,
            id: String(u.id || u._id || `user${idx}`),
            username: String(u.username || (String(u.email||'').split('@')[0] || `user${idx}`)),
          }));
          setUsers(normalized);
          try { localStorage.setItem('users', JSON.stringify(normalized)); } catch {}
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Normalize user IDs/usernames once to ensure selection logic is per-user, not all-at-once
  useEffect(() => {
    setUsers(prev => {
      const byKey = new Map<string, any>();
      const seenIds = new Set<string>();
      const norm = prev.map((u:any, idx:number) => {
        const emailKey = String(u.email || '').toLowerCase();
        let username = u.username;
        if (!username || username === 'undefined') {
          const fromEmail = emailKey ? emailKey.split('@')[0] : '';
          const fromName = u.name ? String(u.name).toLowerCase().replace(/\s+/g, '') : '';
          username = fromEmail || fromName || `user${idx}`;
        }
        let id = String(u.id || u._id || username);
        if (!id || id === 'undefined') id = `${username}_${idx}`;
        while (seenIds.has(id)) id = `${id}_${idx}`;
        seenIds.add(id);
        const merged = { ...u, id, username };
        const key = emailKey || username || id;
        if (!byKey.has(key)) byKey.set(key, merged);
        return merged;
      });
      const result:any[] = [];
      const emitted = new Set(byKey.keys());
      for (const u of norm) {
        const key = String(u.email || '').toLowerCase() || u.username || u.id;
        if (emitted.has(key)) { result.push(byKey.get(key)); emitted.delete(key); }
      }
      try { localStorage.setItem('users', JSON.stringify(result)); } catch {}
      return result;
    });
  // run once after mount
   
  }, []);

  // Lazy-load minimal cohorts for composing when SMS/Email tabs are opened
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
  if (tab === 'sms' && !cohortsLoaded.sms) {
          const [existing, non] = await Promise.allSettled([
            fetchCohort('leads_existing'),
            fetchCohort('leads_non'),
          ]);
          const items = [
            ...(existing.status==='fulfilled' && existing.value && Array.isArray((existing.value as any).items) ? (existing.value as any).items : []),
            ...(non.status==='fulfilled' && non.value && Array.isArray((non.value as any).items) ? (non.value as any).items : []),
          ];
          if (!cancelled) {
            setLeads(items as any);
            setCohortsLoaded(prev => ({ ...prev, sms: true }));
          }
        }
  if (tab === 'email' && !cohortsLoaded.email) {
          const [existing, non] = await Promise.allSettled([
            fetchCohort('leads_existing'),
            fetchCohort('leads_non'),
          ]);
          const items = [
            ...(existing.status==='fulfilled' && existing.value && Array.isArray((existing.value as any).items) ? (existing.value as any).items : []),
            ...(non.status==='fulfilled' && non.value && Array.isArray((non.value as any).items) ? (non.value as any).items : []),
          ];
          if (!cancelled) {
            setLeads(items as any);
            setCohortsLoaded(prev => ({ ...prev, email: true }));
          }
        }
      } catch {
        if (!cancelled) setLeads([...(mockLeads as any[])]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [tab, cohortsLoaded]);

  // Load managers to detect System Admin card (by email), available to at-least basic admins
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const managers: any[] = await (fetchJson('/api/admin/managers') as Promise<any>).catch(() => []) as any[];
        const emails = (managers || [])
          .filter((m:any) => String(m.role).toLowerCase() === 'superadmin')
          .map((m:any) => String(m.email || '').toLowerCase())
          .filter(Boolean);
        if (!ignore) setSuperAdminEmails(emails);
      } catch {}
      finally { if (!ignore) setManagersLoaded(true); }
    })();
    return () => { ignore = true; };
  }, []);

  const isSystemAdminCard = (u: any) => {
    const email = String(u?.email || '').toLowerCase();
    return !!email && superAdminEmails.includes(email);
  };

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [customizeUserId, setCustomizeUserId] = useState<string | null>(null);
  // Guard: if modal opens on restricted user, auto-close and notify
  useEffect(() => {
    if (!customizeUserId) return;
    const u = users.find((x:any) => x.id === customizeUserId);
    if (!managersLoaded) return; // wait until we know
    if (u && isSystemAdminCard(u)) {
      setCustomizeUserId(null);
      safeToast('info', 'Basic Admin cannot customize System Admin card');
    }
  }, [customizeUserId, managersLoaded, superAdminEmails, users]);
  // Local selection state (used when parent does not control selection)
  const [localSelectedUsers, setLocalSelectedUsers] = useState<string[]>([]);
  // Effective selection prefers parent-provided value when present
  const selectedUsers = (providedSelectedUsers && Array.isArray(providedSelectedUsers)) ? providedSelectedUsers : localSelectedUsers;
  // Back-compat setter wrapper: when controlled externally, diff and emit toggle events; else update local state
  const setSelectedUsers = (nextOrUpdater: string[] | ((prev: string[]) => string[])) => {
    if (Array.isArray(providedSelectedUsers)) {
      try {
        const next = typeof nextOrUpdater === 'function' ? (nextOrUpdater as any)(providedSelectedUsers) : nextOrUpdater;
        const currentSet = new Set(providedSelectedUsers);
        const nextSet = new Set(next);
        // toggles: additions and removals
          (next as string[]).forEach((id: string) => { if (!currentSet.has(id)) { if (onUserSelect) onUserSelect(id); } });
          (providedSelectedUsers as string[]).forEach((id: string) => { if (!nextSet.has(id)) { if (onUserSelect) onUserSelect(id); } });
      } catch { /* noop */ }
    } else {
      setLocalSelectedUsers(nextOrUpdater as any);
    }
  };
  // Helper: when a row checkbox is clicked (toggle)
  const handleRowSelect = (userId: string) => {
    if (onUserSelect) onUserSelect(userId); else setLocalSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [userId, ...prev]);
  };

  // Debug: visible verification badge for superadmins when enabled by localStorage flag
  const [debugBadgeEnabled, setDebugBadgeEnabled] = useState<boolean>(() => {
    try { return ((localStorage.getItem('admin_debug_badge') || 'show') === 'show'); } catch { return true; }
  });
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'admin_debug_badge') {
        setDebugBadgeEnabled((e.newValue || 'show') === 'show');
      }
    };
    try { window.addEventListener('storage', onStorage); } catch {}
    return () => { try { window.removeEventListener('storage', onStorage); } catch {} };
  }, []);
  const isSuper = useMemo(() => {
    try {
      const u = (currentUser ?? (JSON.parse(localStorage.getItem('CURRENT_USER') || 'null'))) as any;
      const role = String(u?.role || u?.userRole || '').toLowerCase();
      return role.includes('super') || u?.isSuperAdmin === true || u?.superadmin === true;
    } catch { return false; }
  }, [currentUser]);
  // Removed: bottom-right floating debug toast badge in production
  // Helper: when explicit preview/customize should drive primary preview
  const handlePrimarySelect = (userId: string) => {
    if (onSelectedUserChange) onSelectedUserChange(userId);
    else if (onUserSelect) onUserSelect(userId);
    else setLocalSelectedUsers(prev => [userId, ...prev.filter(id => id !== userId)]);
  };
  const [bulkTemplate, setBulkTemplate] = useState('1');

  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [adModalOpen, setAdModalOpen] = useState(false);
  const [tplModalOpen, setTplModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any | null>(null);
  const [editingAd, setEditingAd] = useState<any | null>(null);
  const [editingTpl, setEditingTpl] = useState<any | null>(null);
  const [serverTemplates, setServerTemplates] = useState<ServerTemplateItem[]>([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplFilterApproval, setTplFilterApproval] = useState<'all'|'approved'|'pending'|'rejected'>('all');
  const [tplCategory, setTplCategory] = useState<'business'|'artist'>('business');
  const [tplName, setTplName] = useState('');
  const [tplDesc, setTplDesc] = useState('');
  const [tplPreviewUrl, setTplPreviewUrl] = useState('');
  const [tplBundleFile, setTplBundleFile] = useState<File | null>(null);
  const [tplTCardFile, setTplTCardFile] = useState<File | null>(null);
  const [tplSubmitting, setTplSubmitting] = useState(false);
  const [tplPreviewMeta, setTplPreviewMeta] = useState<Partial<ServerTemplateItem> | null>(null);
  const [tplPreviewLoading, setTplPreviewLoading] = useState(false);
  const [peekTplId, setPeekTplId] = useState<string | null>(null);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [exportPendingIds, setExportPendingIds] = useState<string[] | null>(null);
  const [newAd, setNewAd] = useState<any>(null);
  const [subSummary, setSubSummary] = useState<any>({ total: 0, active: 0, inactive: 0, gold: 0, silver: 0, trial: 0, expired: 0 });
  const [expiredUsers, setExpiredUsers] = useState<any[]>([]);
  const [expiredEmail, setExpiredEmail] = useState('');
  const [expiredUsername, setExpiredUsername] = useState('');
  const [expiredPlan, setExpiredPlan] = useState<string>('all');
  const [expiredDate, setExpiredDate] = useState('');
  const [expiredPage, setExpiredPage] = useState(1);

  const expiredPlanOptions = useMemo(() => {
    const set = new Set<string>();
    for (const u of expiredUsers) {
      const t = String(u?.subscription?.type || '').trim();
      if (t) set.add(t);
    }
    return Array.from(set).sort();
  }, [expiredUsers]);

  const filteredExpiredUsers = useMemo(() => {
    let list = expiredUsers;
    const e = expiredEmail.trim().toLowerCase();
    const u = expiredUsername.trim().toLowerCase();
    const p = expiredPlan;
    const d = expiredDate.trim(); // YYYY-MM-DD
    if (e) list = list.filter((x:any) => String(x.email||'').toLowerCase().includes(e));
    if (u) list = list.filter((x:any) => String(x.username||'').toLowerCase().includes(u));
    if (p && p !== 'all') list = list.filter((x:any) => String(x?.subscription?.type||'') === p);
    if (d) list = list.filter((x:any) => {
      const ex = x?.subscription?.expiresAt ? new Date(x.subscription.expiresAt) : null;
      if (!ex) return false;
      return ex.toISOString().slice(0,10) === d;
    });
    return list;
  }, [expiredUsers, expiredEmail, expiredUsername, expiredPlan, expiredDate]);

  const expiredItemsPerPage = 20;
  const expiredTotalPages = Math.max(1, Math.ceil(filteredExpiredUsers.length / expiredItemsPerPage));
  const expiredPageSafe = Math.min(Math.max(1, expiredPage), expiredTotalPages);
  const expiredSlice = useMemo(() => {
    const start = (expiredPageSafe - 1) * expiredItemsPerPage;
    return filteredExpiredUsers.slice(start, start + expiredItemsPerPage);
  }, [filteredExpiredUsers, expiredPageSafe]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [showIncompleteMenu, setShowIncompleteMenu] = useState(false);
  // Ads Filters (simplified: single-day match instead of ranges to reduce UI width)
  const [adFilterTitle, setAdFilterTitle] = useState('');
  const [adFilterType, setAdFilterType] = useState<'all'|'free'|'paid'>('all');
  const [adFilterCreatedOn, setAdFilterCreatedOn] = useState('');
  const [adFilterEndedOn, setAdFilterEndedOn] = useState('');

  // Build per-ad performance summary for reporting
  function buildAdPerformance(ad: any) {
    const views = Number(ad?.analytics?.views || ad?.views || 0);
    const clicks = Number(ad?.clicks || 0);
    const impressions = Number(ad?.analytics?.impressions || ad?.impressions || 0);
    const budgetSpent = Number(ad?.billing?.budgetSpent || 0);
    const start = ad?.schedule?.startDate ? new Date(ad.schedule.startDate) : undefined;
    const end = ad?.schedule?.endDate ? new Date(ad.schedule.endDate) : undefined;
    let runtimeDays = 0;
    if (start && end && !isNaN(+start) && !isNaN(+end)) {
      const ms = Math.max(0, (+end - +start));
      runtimeDays = Math.ceil(ms / (1000*60*60*24));
    }
    const audience = {
      ages: (ad?.targetAudience?.ageRange || []).join(', '),
      genders: (ad?.targetAudience?.gender || []).join(', '),
      locations: (ad?.targetAudience?.location || []).join(', '),
      jobs: (ad?.targetAudience?.jobTitles || []).join(', '),
    };
    const ctr = views > 0 ? (clicks / Math.max(1, views)) * 100 : 0;
    return { views, clicks, impressions, budgetSpent, runtimeDays, audience, ctr: Number(ctr.toFixed(1)) };
  }

  // Export filtered ads to CSV
  function downloadAdReportCSV(rows: any[]) {
    try {
      const header = ['Title','Type','Created','Ended','Views','Clicks','CTR %','Impressions','Budget Spent','Ages','Genders','Locations','Jobs'];
      const body = rows.map((ad:any) => {
        const perf = buildAdPerformance(ad);
        const created = ad?.createdAt || ad?.updatedAt || '';
        const ended = ad?.schedule?.endDate || '';
        const type = (ad?.adType || (((ad?.billing?.budgetTotal || 0) > 0) ? 'paid' : 'free')) as string;
        return [
          String(ad?.title || ad?.id || ''),
          type,
          created,
          ended,
          String(perf.views),
          String(perf.clicks),
          String(perf.ctr),
          String(perf.impressions),
          String(perf.budgetSpent),
          perf.audience.ages,
          perf.audience.genders,
          perf.audience.locations,
          perf.audience.jobs,
        ];
      });
      const csv = [header, ...body].map(r => r.map((c:any) => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `ads-report-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
      safeToast('success','Ads CSV downloaded');
    } catch { safeToast('error','Failed to export ads CSV'); }
  }

  // Export filtered ads to PDF
  function downloadAdReportPDF(rows: any[]) {
    try {
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Advertisements Report', 14, 16);
      autoTable(doc, {
        startY: 22,
        head: [[
          'Title','Type','Created','Ended','Views','Clicks','CTR %','Impressions','Budget Spent','Ages','Genders','Locations','Jobs'
        ]],
        body: rows.map((ad:any) => {
          const perf = buildAdPerformance(ad);
          const created = ad?.createdAt || ad?.updatedAt || '';
          const ended = ad?.schedule?.endDate || '';
          const type = (ad?.adType || (((ad?.billing?.budgetTotal || 0) > 0) ? 'paid' : 'free')) as string;
          return [
            String(ad?.title || ad?.id || ''),
            String(type || ''),
            String(created || ''),
            String(ended || ''),
            String(perf.views),
            String(perf.clicks),
            String(perf.ctr),
            String(perf.impressions),
            String(perf.budgetSpent),
            String(perf.audience.ages || ''),
            String(perf.audience.genders || ''),
            String(perf.audience.locations || ''),
            String(perf.audience.jobs || ''),
          ];
        }),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [30, 41, 59] },
        columnStyles: { 0: { cellWidth: 42 } } as any,
      });
      doc.save(`ads-report-${Date.now()}.pdf`);
      safeToast('success','Ads PDF downloaded');
    } catch { safeToast('error','Failed to export ads PDF'); }
  }

  // Export single ad as compact PDF
  function downloadSingleAdPDF(ad:any) {
    try {
      const doc = new jsPDF();
      const perf = buildAdPerformance(ad);
      doc.setFontSize(16);
      doc.text(String(ad?.title || ad?.id || 'Advertisement'), 14, 16);
      autoTable(doc, {
        startY: 22,
        head: [['Metric','Value']],
        body: [
          ['Type', String(ad?.adType || (((ad?.billing?.budgetTotal || 0) > 0) ? 'paid' : 'free'))],
          ['Created', String(ad?.createdAt || ad?.updatedAt || '')],
          ['Ended', String(ad?.schedule?.endDate || '')],
          ['Views', String(perf.views)],
          ['Clicks', String(perf.clicks)],
          ['CTR %', String(perf.ctr)],
          ['Impressions', String(perf.impressions)],
          ['Budget Spent', String(perf.budgetSpent)],
          ['Runtime (days)', String(perf.runtimeDays)],
          ['Ages', perf.audience.ages],
          ['Genders', perf.audience.genders],
          ['Locations', perf.audience.locations],
          ['Jobs', perf.audience.jobs],
        ],
        styles: { fontSize: 10 },
        headStyles: { fillColor: [99, 102, 241] },
      });
      doc.save(`ad-${String(ad?.id || ad?.title || 'report')}-${Date.now()}.pdf`);
      safeToast('success','Ad report downloaded');
    } catch { safeToast('error','Failed to export ad report'); }
  }

  // Minimal inline password reset for admins (uses existing API)
  async function resetUserPasswordInline(user: any) {
    try {
      const token = localStorage.getItem('dev_admin_token');
      if (!token) { safeToast('error', 'Admin session required'); return; }
      const suggested = (user?.email ? `${user.email.split('@')[0]}123!` : 'NewPass123!');
      const next = window.prompt(`Set new password for ${user?.name || user?.email || user?.username}\n(At least 6 characters recommended)`, suggested);
      if (!next) return; // cancelled
      const key = encodeURIComponent(String(user?.id || user?.username || ''));
      if (!key) { safeToast('error', 'Invalid user identifier'); return; }
      const res = await fetch(`/api/admin/users/${key}/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: next })
      });
      if (!res.ok) {
        const err = await res.json().catch(()=>({}));
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      safeToast('success', 'Password updated');
    } catch (e:any) {
      safeToast('error', `Failed to set password: ${e?.message || e}`);
    }
  }

  function initialNewAd() {
    return {
      title: '',
      description: '',
      link: '',
      image: '',
      targetAudience: { ageRange: [], gender: [], location: [], jobTitles: [] },
      schedule: { startDate: '', endDate: '', startTime: '09:00', endTime: '18:00', timezone: 'UTC' },
      clicks: 0,
      analytics: { views: 0, impressions: 0 },
      adType: 'free',
      billing: { currency: 'USD', budgetTotal: 0, budgetSpent: 0, cpm: 0, cpc: 0, paymentStatus: 'unpaid' },
      isActive: true,
    };
  }

  useEffect(() => { setNewAd(initialNewAd()); }, []);
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoadingSubs(true);
        const [sum, exp] = await Promise.allSettled([getSubscriptionSummary(), listExpiredSubscriptions()]);
        if (!ignore) {
          if (sum.status === 'fulfilled' && sum.value) setSubSummary(sum.value);
          if (exp.status === 'fulfilled' && Array.isArray(exp.value)) setExpiredUsers(exp.value as any[]);
        }
      } finally { if (!ignore) setLoadingSubs(false); }
    })();
    return () => { ignore = true; };
  }, []);

  const handleAddAd = () => {
    try {
      const ad = { ...(newAd || {}), id: `ad_${Date.now()}`, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      handleSaveAd(ad);
      recordActivity(`Created advertisement ${ad.title || ad.id}`);
      setNewAd(initialNewAd());
      safeToast('success', 'Advertisement created');
    } catch (e) {
      safeToast('error', 'Failed to create advertisement');
    }
  };

  function getUniqueLocations() {
    const locations = new Set<string>();
    (users || []).forEach(u => { if (u.location) locations.add(u.location); });
    return Array.from(locations).slice(0, 20);
  }

  function getUniqueJobTitles() {
    const titles = new Set<string>();
    (users || []).forEach(u => { if (u.position) titles.add(u.position); });
    return Array.from(titles).slice(0, 40);
  }

  const [pending, setPending] = useState<any[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_PENDING_KEY) || '[]');
    } catch (e) {
      return [];
    }
  });
  const [activities, setActivities] = useState<AdminActivity[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(ACTIVITY_LOG_KEY) || '[]');
    } catch (e) {
      return [];
    }
  });
  const [homepageSettings, setHomepageSettings] = useState<any>(() => {
    try {
      return JSON.parse(localStorage.getItem('basic_admin_homepage') || '{}');
    } catch (e) {
      return {};
    }
  });

  useEffect(() => { localStorage.setItem(LOCAL_PENDING_KEY, JSON.stringify(pending)) }, [pending]);
  useEffect(() => { localStorage.setItem(ACTIVITY_LOG_KEY, JSON.stringify(activities)) }, [activities]);
  useEffect(() => { localStorage.setItem('basic_admin_homepage', JSON.stringify(homepageSettings)) }, [homepageSettings]);

  useEffect(() => {
    let mounted = true;
    getCurrentUser().then((u:any) => { if (!mounted) return; if (u) { setCurrentUser(u); try { localStorage.setItem('CURRENT_USER', JSON.stringify(u)); } catch {} } }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (tab !== 'templates') return;
      setTplLoading(true);
      try {
        const items = await listServerTemplates({});
        if (!ignore) setServerTemplates(items);
      } catch (e:any) {
        if (!ignore) safeToast('error', e?.message || 'Failed to load templates');
      } finally {
        if (!ignore) setTplLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [tab]);

  const visibleServerTemplates = useMemo(() => {
    const email = String(currentUser?.email || '').toLowerCase();
    const uid = (currentUser as any)?.id || (currentUser as any)?._id;
    const statusFiltered = serverTemplates.filter((t:any) => {
      const status = t.approvalStatus || (t.approved ? 'approved' : undefined);
      if (tplFilterApproval !== 'all' && status !== tplFilterApproval) return false;
      // Visibility: show approved system templates, or templates uploaded by this basic admin
      const own = (String(t.submittedByEmail||'').toLowerCase() === email) || (t.submittedById && uid && String(t.submittedById) === String(uid));
      const isApproved = status === 'approved';
      return isApproved || own;
    });
    return statusFiltered;
  }, [serverTemplates, tplFilterApproval, currentUser]);

  async function loadTplPreview(file: File | null) {
    if (!file) { setTplPreviewMeta(null); return; }
    setTplPreviewLoading(true);
    try {
      const meta = await previewServerTCard(file, { name: tplName || undefined, description: tplDesc || undefined, category: tplCategory });
      setTplPreviewMeta(meta as any);
      if (!tplName && (meta as any)?.name) setTplName((meta as any).name);
      if (!tplDesc && (meta as any)?.description) setTplDesc((meta as any).description);
      if ((meta as any)?.category && (meta as any).category !== tplCategory) setTplCategory((meta as any).category);
    } catch (e:any) {
      safeToast('error', e?.message || 'Failed to preview template');
    } finally { setTplPreviewLoading(false); }
  }

  async function handleTplSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      setTplSubmitting(true);
      if (tplTCardFile) {
        const created = await uploadServerTCard(tplTCardFile, { name: tplName || undefined, description: tplDesc || undefined, category: tplCategory });
        setServerTemplates(prev => [created, ...prev]);
        setTplPreviewMeta(null);
      } else {
        let uploaded: { url?: string } = {};
        if (tplBundleFile) {
          const up = await uploadServerBundle(tplBundleFile);
          uploaded = up || {};
        }
        const created = await createServerTemplate({ name: tplName, description: tplDesc, category: tplCategory, active: false, previewUrl: tplPreviewUrl, bundleUrl: uploaded.url || '' } as any);
        setServerTemplates(prev => [created, ...prev]);
      }
      setTplName(''); setTplDesc(''); setTplPreviewUrl(''); setTplBundleFile(null); setTplTCardFile(null);
      safeToast('success', 'Template added');
    } catch (e:any) {
      safeToast('error', e?.message || 'Failed to add template');
    } finally { setTplSubmitting(false); }
  }

  const recordActivity = (text: string) => {
    const adminName = (currentUser && (currentUser.name || (currentUser as any).email)) || (() => {
      try {
        const raw = localStorage.getItem('CURRENT_USER');
        return raw ? (JSON.parse(raw).name || JSON.parse(raw).email || 'BasicAdmin') : 'BasicAdmin';
      } catch { return 'BasicAdmin'; }
    })();
    const entry: AdminActivity = { id: `a_${Date.now()}`, admin: String(adminName), action: text, at: new Date().toISOString() };
    setActivities(prev => [entry, ...prev]);
  };

  const submitBulkRequest = async (payload: any) => {
    const next = [{ ...payload, id: `b_${Date.now()}`, status: 'pending', createdAt: new Date().toISOString() }, ...pending];
    setPending(next);
    try {
      const q = JSON.parse(localStorage.getItem(SUPERADMIN_QUEUE_KEY) || '[]');
      localStorage.setItem(SUPERADMIN_QUEUE_KEY, JSON.stringify([{ ...payload, id: `b_${Date.now()}`, type: payload.type, status: 'pending', createdAt: new Date().toISOString() }, ...q]));
      // Also create a server-side BulkRequest so it appears in System Admin notifications
      try {
        await createBulkRequest(payload);
      } catch (e:any) {
        // Surface a friendly message while keeping the local queue entry
        safeToast('error', e?.message || 'Failed to create server bulk request');
      }
    } catch (e) {}
    safeToast('success', 'Request submitted for Super Admin review');
    recordActivity(`${payload.type === 'sms' ? 'Bulk SMS' : 'Bulk Email'} composed and submitted for review (${payload.recipients?.length || 0} recipients)`);
  };

  const handleSavePlan = (plan: any) => {
    setHomepageSettings((prev:any) => {
      const next: any = { ...(prev || {}) };
      next.pricing = { ...(prev.pricing || defaultHomepageSettings.pricing) };
      const existing = Array.isArray(next.pricing.plans) ? [...next.pricing.plans] : [...defaultHomepageSettings.pricing.plans];
      if (!plan.id) plan.id = `p_${Date.now()}`;
      plan.createdAt = plan.createdAt || new Date().toISOString();
      plan.updatedAt = new Date().toISOString();
      const idx = existing.findIndex((p:any) => p.id === plan.id);
      if (idx >= 0) existing[idx] = plan; else existing.unshift(plan);
      next.pricing.plans = existing;
      return next;
    });
    recordActivity(`Saved subscription plan ${plan.name || plan.id}`);
    safeToast('success', 'Plan saved to preview');
  };

  const handleSaveAd = (ad: any) => {
    setHomepageSettings((prev:any) => {
      const next: any = { ...(prev || {}) };
      next.ads = Array.isArray(next.ads) ? [...next.ads] : [];
      if (!ad.id) ad.id = `ad_${Date.now()}`;
      ad.createdAt = ad.createdAt || new Date().toISOString();
      ad.updatedAt = new Date().toISOString();
      ad.clicks = ad.clicks || 0;
      ad.redirects = ad.redirects || 0;
      ad.analytics = ad.analytics || { views: Math.floor(Math.random()*500)+100, clicks: ad.clicks || 0, impressions: Math.floor(Math.random()*1000)+200, ctr: '0.0' };
      const idx = next.ads.findIndex((a:any) => a.id === ad.id);
      if (idx >= 0) next.ads[idx] = ad; else next.ads.unshift(ad);
      return next;
    });
    recordActivity(`Saved advertisement ${ad.title || ad.id}`);
    safeToast('success', 'Ad saved to preview');
    // queue ad for superadmin review before publishing
    try {
      const q = JSON.parse(localStorage.getItem(SUPERADMIN_QUEUE_KEY) || '[]');
      localStorage.setItem(SUPERADMIN_QUEUE_KEY, JSON.stringify([{ id: `rq_${Date.now()}`, type: 'ad', payload: ad, status: 'pending', createdAt: new Date().toISOString(), createdBy: 'basic_admin' }, ...q]));
    } catch (e) {}
  };

  const deleteSubscriptionPlan = (planId: string) => {
    // only allow deletion of locally created plans (id starts with p_)
    if (!planId || !planId.startsWith('p_')) { safeToast('error','Cannot delete system subscription plan'); return; }
    setHomepageSettings((prev:any) => {
      const next: any = { ...(prev || {}) };
      next.pricing = { ...(prev.pricing || defaultHomepageSettings.pricing) };
      next.pricing.plans = Array.isArray(next.pricing.plans) ? next.pricing.plans.filter((p:any)=>p.id !== planId) : [];
      return next;
    });
    recordActivity(`Deleted subscription plan ${planId}`);
    safeToast('success','Subscription plan removed from preview');
    // queue deletion note for superadmin (for audit)
    try { const q = JSON.parse(localStorage.getItem(SUPERADMIN_QUEUE_KEY) || '[]'); localStorage.setItem(SUPERADMIN_QUEUE_KEY, JSON.stringify([{ id: `rq_${Date.now()}`, type: 'plan_delete', payload: { id: planId }, status: 'pending', createdAt: new Date().toISOString(), createdBy: 'basic_admin' }, ...q])); } catch (e) {}
  };

  const handleSaveTemplate = (tpl: any) => {
    setHomepageSettings((prev:any) => {
      const next: any = { ...(prev || {}) };
      next.templates = Array.isArray(next.templates) ? [...next.templates] : [];
      if (!tpl.id) tpl.id = `t_${Date.now()}`;
      tpl.createdAt = tpl.createdAt || new Date().toISOString();
      tpl.updatedAt = new Date().toISOString();
      const idx = next.templates.findIndex((t:any) => t.id === tpl.id);
      if (idx >= 0) next.templates[idx] = tpl; else next.templates.unshift(tpl);
      return next;
    });
    recordActivity(`Saved template ${tpl.name || tpl.id}`);
    safeToast('success', 'Template saved to preview');
  };

  // Prefer edited preview from local basic_admin_homepage; fallback to mockAds then defaultHomepageSettings.ads
  // Real ads from server (admin/full list) loaded when ads tab first opened; fallback to preview/mock
  const [serverAds, setServerAds] = useState<AdminAd[]>([]);
  const [adsLoading, setAdsLoading] = useState(false);
  // Real subscription plans from server; loaded lazily when subscriptions tab opens
  const [serverPlans, setServerPlans] = useState<AdminSubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  useEffect(() => {
    if (tab !== 'ads') return; // only load when viewing ads
    if (serverAds.length > 0) return; // load once per mount
    let ignore = false;
    (async () => {
      setAdsLoading(true);
      try {
        const list = await listAdminAds();
        if (!ignore && Array.isArray(list)) setServerAds(list as AdminAd[]);
      } finally { if (!ignore) setAdsLoading(false); }
    })();
    return () => { ignore = true; };
  }, [tab, serverAds.length]);

  const previewAds: AdminAd[] = (homepageSettings && Array.isArray(homepageSettings.ads) && homepageSettings.ads.length ? (homepageSettings.ads as AdminAd[]) : ((mockAds as AdminAd[])?.length ? (mockAds as AdminAd[]) : (((defaultHomepageSettings as any).ads || []))));
  // Merge server ads (authoritative) with any local preview ads not in server list (id mismatch) for display
  const adsList: AdminAd[] = useMemo(() => {
    if (serverAds.length === 0) return previewAds;
    const existingIds = new Set(serverAds.map(a => getId(a)));
    const extras = previewAds.filter(a => a && !existingIds.has(getId(a)));
    return [...serverAds, ...extras];
  }, [serverAds, previewAds]);

  // Derived: filtered ads according to UI filters
  const filteredAds: AdminAd[] = useMemo(() => {
    const normalizeType = (ad:any) => (ad?.adType || (((ad?.billing?.budgetTotal || 0) > 0) ? 'paid' : 'free')) as 'free'|'paid';
    return (adsList || []).filter((ad:any) => {
      const titleOk = adFilterTitle ? String(ad?.title || '').toLowerCase().includes(adFilterTitle.toLowerCase()) : true;
      const typeOk = (adFilterType === 'all') ? true : (normalizeType(ad) === adFilterType);
      const createdISO = String(ad?.createdAt || ad?.updatedAt || '').slice(0,10);
      const endedISO = String(ad?.schedule?.endDate || '').slice(0,10);
      const createdOk = adFilterCreatedOn ? createdISO === adFilterCreatedOn : true;
      const endedOk = adFilterEndedOn ? endedISO === adFilterEndedOn : true;
      return titleOk && typeOk && createdOk && endedOk;
    });
  }, [adsList, adFilterTitle, adFilterType, adFilterCreatedOn, adFilterEndedOn]);

  // Subscription plans: show ONLY server-provided plans (authoritative). Hide all mock/preview ones.
  const plansList: AdminSubscriptionPlan[] = useMemo(() => {
    return serverPlans.length > 0 ? serverPlans : [];
  }, [serverPlans]);

  // Lazy load subscription plans when subscriptions tab is first opened; purge any local mock plans once loaded
  useEffect(() => {
    if (tab !== 'subscriptions') return;
    if (serverPlans.length > 0) return; // load once
    let ignore = false;
    (async () => {
      setPlansLoading(true);
      try {
        const list = await listSubscriptionPlans();
        if (!ignore && Array.isArray(list)) {
          setServerPlans(list as AdminSubscriptionPlan[]);
          // Purge local mock/default plans; replace with server list for any logic relying on homepageSettings
          setHomepageSettings((prev:any) => {
            const next = { ...(prev || {}) };
            next.pricing = { ...(next.pricing || {}) };
            next.pricing.plans = list; // overwrite entirely with server plans
            try { localStorage.setItem('basic_admin_homepage', JSON.stringify(next)); } catch {}
            return next;
          });
        }
      } catch (e:any) {
        if (!ignore) safeToast('error', e?.message || 'Failed to load subscription plans');
      } finally { if (!ignore) setPlansLoading(false); }
    })();
    return () => { ignore = true; };
  }, [tab, serverPlans.length]);

  const toggleAdActive = (adId: string) => {
    setHomepageSettings((prev:any) => {
      const next = { ...(prev || {}) };
      next.ads = Array.isArray(next.ads) ? next.ads.map((a:any) => a.id === adId ? { ...a, isActive: !a.isActive } : a) : [];
      return next;
    });
    const ad = adsList.find(a => a.id === adId);
    recordActivity(`Toggled ad ${ad?.title || adId} active state`);
  };

  const togglePlanActive = (planId: string) => {
    setHomepageSettings((prev:any) => {
      const next = { ...(prev || {}) };
      next.pricing = { ...(prev.pricing || defaultHomepageSettings.pricing) };
      next.pricing.plans = Array.isArray(next.pricing.plans) ? next.pricing.plans.map((p:any) => p.id === planId ? { ...p, isActive: !p.isActive } : p) : [];
      return next;
    });
    const plan = plansList.find(p => p.id === planId);
    recordActivity(`Toggled subscription plan ${plan?.name || planId} active state`);
  };

  // Subscription editing state (Basic Admin can edit preview but not delete)
  const [editingSubscriptionPlans, setEditingSubscriptionPlans] = useState<boolean>(false);

  const addSubscriptionPlan = (plan: any) => {
    setHomepageSettings((prev:any) => {
      const next: any = { ...(prev || {}) };
      next.pricing = { ...(prev.pricing || defaultHomepageSettings.pricing) };
      const existingPlans = Array.isArray(next.pricing.plans) ? [...next.pricing.plans] : [...defaultHomepageSettings.pricing.plans];
      // Remove any existing unsaved temp plan (id starting with p_tmp_)
      const filtered = existingPlans.filter((p:any) => !(p && typeof p.id === 'string' && p.id.startsWith('p_tmp_')));
      const tempId = `p_tmp_${Date.now()}`;
      const newPlan = { ...plan, id: tempId, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      next.pricing.plans = [newPlan, ...filtered];
      return next;
    });
    recordActivity(`Added subscription plan ${plan.name || ''}`);
    safeToast('success','Subscription plan added to preview');
    // queue for super admin review
    try {
      const q = JSON.parse(localStorage.getItem(SUPERADMIN_QUEUE_KEY) || '[]');
      localStorage.setItem(SUPERADMIN_QUEUE_KEY, JSON.stringify([{ id: `rq_${Date.now()}`, type: 'plan', payload: { ...plan, tempId: 'p_tmp' }, status: 'pending', createdAt: new Date().toISOString(), createdBy: 'basic_admin' }, ...q]));
    } catch (e) {}
  };

  const updateSubscriptionPlan = (planId: string, updates: any) => {
    setHomepageSettings((prev:any) => {
      const next: any = { ...(prev || {}) };
      next.pricing = { ...(prev.pricing || defaultHomepageSettings.pricing) };
      next.pricing.plans = Array.isArray(next.pricing.plans) ? next.pricing.plans.map((p:any) => p.id === planId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p) : [];
      return next;
    });
    recordActivity(`Updated subscription plan ${updates.name || planId}`);
    // queue update for review (optional) - mark as pending update
    try {
      const q = JSON.parse(localStorage.getItem(SUPERADMIN_QUEUE_KEY) || '[]');
      localStorage.setItem(SUPERADMIN_QUEUE_KEY, JSON.stringify([{ id: `rq_${Date.now()}`, type: 'plan_update', payload: { id: planId, updates }, status: 'pending', createdAt: new Date().toISOString(), createdBy: 'basic_admin' }, ...q]));
    } catch (e) {}
  };

  const exportUsersCSV = (userIds?: string[]) => {
    try {
      const selected = userIds && userIds.length ? users.filter(u => userIds.includes(getId(u))) : users;
      const csvData = [
        ['User ID','Name','Email','Company','Subscription','Views','Active'],
        ...selected.map((u:any) => [u.id, u.name, u.email || '', u.company || '', u.subscription?.type || '', u.analytics?.views || 0, u.isActive ? 'true' : 'false'])
      ];
      const csv = csvData.map(r => r.map((c:any) => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `users-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
      safeToast('success','Users exported');
    } catch (e) { safeToast('error','Failed to export users'); }
  };

  // Generate a concise PDF with key metrics only
  const downloadBasicStatsPDF = () => {
    try {
      const totalUsers = displayedTotalUsers;
      const activeUsers = displayedActiveUsers;
      const leadsCount = leads.length;
      const totalViews = Number((serverSystemStats as any)?.totalViews ?? (Number((users||[]).reduce((s,u)=>s+Number(u?.analytics?.views||0),0)) || 0));
      // Use computed fallback totalShares for consistency
      const sharesValue = Number(totalShares || 0);

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Basic Statistics', 14, 16);
      autoTable(doc, {
        startY: 22,
        head: [['Metric', 'Value']],
        body: [
          ['Total Users', String(totalUsers)],
          ['Active Users', String(activeUsers)],
          ['Leads', String(leadsCount)],
          ['Total Views', totalViews.toLocaleString()],
          ['Total Shares', sharesValue.toLocaleString()],
        ],
        styles: { fontSize: 10 },
        headStyles: { fillColor: [30, 41, 59] },
      });
      doc.save(`basic-stats-${Date.now()}.pdf`);
      recordActivity('Downloaded basic stats PDF');
      safeToast('success','Basic stats PDF downloaded');
    } catch (e) {
      safeToast('error','Failed to generate PDF');
    }
  };

  // Generate a comprehensive PDF with overview + users + leads
  const downloadComprehensiveStatsPDF = () => {
    try {
      const totalUsers = displayedTotalUsers;
      const activeUsers = displayedActiveUsers;
      const leadsCount = leads.length;
      const totalViews = Number((serverSystemStats as any)?.totalViews ?? (Number((users||[]).reduce((s,u)=>s+Number(u?.analytics?.views||0),0)) || 0));
      // Use computed fallback totalShares for consistency
      const sharesValue = Number(totalShares || 0);
      const totalAdClicks = adsList.reduce((s,a)=> s + (a.clicks || 0), 0);
      const totalAdRedirects = adsList.reduce((s,a)=> s + (a.redirects || 0), 0);

  const doc = new jsPDF({ orientation: 'landscape' });
      let y = 16;
      doc.setFontSize(16);
      doc.text('Comprehensive Statistics', 14, y);
      y += 6;

      // Overview
      autoTable(doc, {
        startY: y,
        head: [['Metric', 'Value']],
        body: [
          ['Total Users', String(totalUsers)],
          ['Active Users', String(activeUsers)],
          ['Leads', String(leadsCount)],
          ['Total Views', totalViews.toLocaleString()],
          ['Total Shares', sharesValue.toLocaleString()],
          ['Total Ad Clicks', String(totalAdClicks)],
          ['Total Ad Redirects', String(totalAdRedirects)],
        ],
        styles: { fontSize: 10 },
        headStyles: { fillColor: [30, 41, 59] },
      });
      // @ts-ignore - injected by autoTable
      y = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 10 : 40;

      // Users table
      doc.setFontSize(12);
      doc.text('Users', 14, y);
      y += 4;
      autoTable(doc, {
        startY: y,
        head: [['ID','Name','Email','Company','Subscription','Views','Active']],
        body: users.map((u:any) => [
          String(u.id),
          String(u.name || ''),
          String(u.email || ''),
          String(u.company || ''),
          String(u.subscription?.type || ''),
          String(u.analytics?.views || 0),
          u.isActive ? 'true' : 'false'
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [2, 132, 199] },
      });
      // @ts-ignore
      y = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 10 : y + 40;

      // Leads table
      doc.setFontSize(12);
      doc.text('Leads', 14, y);
      y += 4;
      autoTable(doc, {
        startY: y,
        head: [['ID','Name','Email','Phone','Source','Existing User']],
        body: (leads as any[]).map((l:any) => [
          String(l.id || l._id || ''),
          String(l.fullName || l.name || ''),
          String(l.email || ''),
          String(l.phoneNumber || l.phone || ''),
          String(l.source || ''),
          (l.isExistingUser || l.existingUserId) ? 'true' : 'false',
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [16, 185, 129] },
      });
      // @ts-ignore
      y = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 10 : y + 40;

      // Ads table
      doc.setFontSize(12);
      doc.text('Advertisements', 14, y);
      y += 4;
      const formatList = (arr?: any[], cap: number = 3) => {
        const a = Array.isArray(arr) ? arr.filter(Boolean).map((v:any)=>String(v)) : [];
        if (a.length <= cap) return a.join(', ');
        return `${a.slice(0, cap).join(', ')} +${a.length-cap} more`;
      };
      const formatSchedule = (ad: any) => {
        const s = ad?.schedule || {};
        const start = [s.startDate, s.startTime].filter(Boolean).join(' ');
        const end = [s.endDate, s.endTime].filter(Boolean).join(' ');
        const tz = s.timezone ? ` ${s.timezone}` : '';
        if (!start && !end) return '—';
        return `${start || '—'} to ${end || '—'}${tz}`;
      };
      autoTable(doc, {
        startY: y,
        head: [[
          'Title','Views','Clicks','CTR','Dates','Locations','Ages','Genders','Job Titles'
        ]],
        body: adsList.map((ad:any) => {
          const views = Number(ad?.analytics?.views || 0);
          const clicks = Number(ad?.clicks || 0);
          const engagement = views > 0 ? `${((clicks/Math.max(views,1))*100).toFixed(1)}%` : '0.0%';
          return [
            String(ad?.title || ad?.id || ''),
            views.toLocaleString(),
            String(clicks),
            engagement,
            formatSchedule(ad),
            formatList(ad?.targetAudience?.location, 4),
            formatList(ad?.targetAudience?.ageRange, 4),
            formatList(ad?.targetAudience?.gender, 4),
            formatList(ad?.targetAudience?.jobTitles, 4),
          ];
        }),
        styles: { fontSize: 8, overflow: 'ellipsize' },
        headStyles: { fillColor: [99, 102, 241], fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 46 }, // Title
          1: { cellWidth: 18, halign: 'right' }, // Views
          2: { cellWidth: 18, halign: 'right' }, // Clicks
          3: { cellWidth: 16, halign: 'right' }, // CTR
          4: { cellWidth: 45 }, // Dates
          5: { cellWidth: 40 }, // Locations
          6: { cellWidth: 28 }, // Ages
          7: { cellWidth: 28 }, // Genders
          8: { cellWidth: 42 }, // Job Titles
        } as any,
      });

      doc.save(`comprehensive-stats-${Date.now()}.pdf`);
      recordActivity('Downloaded comprehensive stats PDF');
      safeToast('success','Comprehensive stats PDF downloaded');
    } catch (e) {
      safeToast('error','Failed to generate PDF');
    }
  };

  const downloadActivityCSV = () => {
    try {
      const header = ['id','admin','action','at'];
      const rows = activities.map((a:any) => [a.id, a.admin || '', a.action || a.text || '', a.at]);
      const csv = [header, ...rows].map(r => r.map((c:any) => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `basic-admin-activities-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
      safeToast('success','Activity CSV downloaded');
    } catch (e) { safeToast('error','Failed to download CSV'); }
  };

  const templates = [
    { id: '1', name: 'Professional Classic' },
    { id: '2', name: 'Elegant Looking' },
    { id: '3', name: 'Modern and Clean' },
  ];

  // Displayed high-level counts: prefer server stats when available
  const displayedTotalUsers = useMemo(() => {
    const fromServer = Number((serverSystemStats as any)?.totalUsers || 0);
    if (fromServer > 0) return fromServer;
    return users.length;
  }, [serverSystemStats, users.length]);
  const displayedActiveUsers = useMemo(() => {
    const fromServer = Number((serverSystemStats as any)?.activeUsers || 0);
    if (fromServer > 0) return fromServer;
    return users.filter(u=>u.isActive).length;
  }, [serverSystemStats, users]);

  // Derive overview metrics; prefer real server stats and only fall back when unavailable
  const totalViews = useMemo(() => {
    // 1) Prefer server-provided system stats
    const fromServer = Number((serverSystemStats as any)?.totalViews || 0);
    if (fromServer > 0) return fromServer;
    // 2) Sum per-user analytics views if available (real-only)
    const fromUsers = (users || []).reduce((sum: number, u: any) => sum + Number(u?.analytics?.views || 0), 0);
    if (fromUsers > 0) return fromUsers;
    // 3) Only if explicitly allowed, use preview/homepage or mock fallbacks
    if (allowMockStats) {
      const fromHomepage = Number((homepageSettings as any)?.stats?.views || 0);
      if (fromHomepage > 0) return fromHomepage;
      const fromMockUsers = (mockUsers as any[]).reduce((sum: number, u: any) => sum + Number(u?.analytics?.views || 0), 0);
      if (fromMockUsers > 0) return fromMockUsers;
      return Number((mockSystemStats as any)?.totalViews || 0);
    }
    // 4) Real-only default: zero when unavailable
    return 0;
  }, [homepageSettings, users, serverSystemStats, allowMockStats]);

  const adViews = useMemo(() => {
    // 0) Prefer server system stats if provided
    const fromServer = Number((serverSystemStats as any)?.totalAdViews || 0);
    if (fromServer > 0) return fromServer;
    // 1) Sum views from the current ads list (real-only if present)
    const fromAds = (adsList || []).reduce((sum: number, a: any) => sum + Number((a as any)?.analytics?.views || (a as any)?.views || 0), 0);
    if (fromAds > 0) return fromAds;
    // 2) Only if explicitly allowed, fall back to mock ads or derived baseline
    if (allowMockStats) {
      const fromMockAds = (mockAds as any[]).reduce((sum: number, a: any) => sum + Number((a as any)?.analytics?.views || (a as any)?.views || 0), 0);
      if (fromMockAds > 0) return fromMockAds;
      return Math.floor(Number(totalViews || 0) * 0.15);
    }
    // 3) Real-only default: zero when unavailable
    return 0;
  }, [adsList, totalViews, serverSystemStats, allowMockStats]);

  // Derive shares; prefer server stats and only fall back when unavailable
  const totalShares = useMemo(() => {
    const fromServer = Number((serverSystemStats as any)?.totalShares || 0);
    if (fromServer > 0) return fromServer;
    const fromUsers = (users || []).reduce((sum: number, u: any) => sum + Number(u?.analytics?.shares || 0), 0);
    if (fromUsers > 0) return fromUsers;
    if (allowMockStats) {
      const fromHomepage = Number((homepageSettings as any)?.stats?.shares || 0);
      if (fromHomepage > 0) return fromHomepage;
      return Number((mockSystemStats as any)?.totalShares || 0);
    }
    return 0;
  }, [homepageSettings, users, serverSystemStats, allowMockStats]);

  // New: totalCallClicks for overview tile (prefer server aggregate; fallback to per-user sum)
  const totalCallClicks = useMemo(() => {
    const fromServer = Number((serverSystemStats as any)?.totalCallClicks || 0);
    if (fromServer > 0) return fromServer;
    const fromUsers = (users || []).reduce((sum: number, u: any) => sum + Number(u?.analytics?.callClicks || 0), 0);
    if (fromUsers > 0) return fromUsers;
    return 0;
  }, [users, serverSystemStats]);

  return (
    <div data-panel="basic-admin-v2" className="p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-lg sm:text-2xl font-bold">Basic Admin Panel</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 relative">
          {(() => {
            const label = liveMode === 'sse' ? 'Live' : liveMode === 'poll' ? 'Polling' : 'Offline';
            const color = liveMode === 'sse' ? 'bg-green-500' : liveMode === 'poll' ? 'bg-amber-500' : 'bg-gray-400';
            const title = liveMode === 'sse' ? 'Connected via SSE. Falls back to polling if blocked.' : liveMode === 'poll' ? 'SSE unavailable; using periodic polling.' : 'Not connected.';
            return (
              <span className="inline-flex items-center text-xs text-gray-600" title={title}>
                <span className={`w-2 h-2 rounded-full mr-1 ${color}`}></span>{label}
              </span>
            );
          })()}
          <button onClick={() => setShowCreateModal(true)} className="px-3 sm:px-4 py-2 bg-blue-600 text-white text-sm rounded"><span className="hidden sm:inline">Create Card User</span><span className="sm:hidden">+ User</span></button>
          {/* Clear Local Cache button moved to System Admin (super admin only) */}
          <button onClick={() => setTab('sms')} className="px-3 py-2 bg-amber-500 text-white text-sm rounded"><span className="hidden sm:inline">Bulk SMS</span><span className="sm:hidden">SMS</span></button>
          <button onClick={() => setTab('email')} className="px-3 py-2 bg-amber-500 text-white text-sm rounded"><span className="hidden sm:inline">Bulk Email</span><span className="sm:hidden">Email</span></button>
          <div>
            <button onClick={() => setShowIncompleteMenu(s=>!s)} className="px-3 py-2 bg-orange-600 text-white text-sm rounded"><span className="hidden sm:inline">Send to Incomplete Signup</span><span className="sm:hidden">Incomplete</span></button>
            {showIncompleteMenu && (
              <div className="absolute right-0 mt-2 bg-white border rounded shadow z-40 w-[calc(100vw-3rem)] sm:w-64 max-w-xs">
                <div className="p-2 text-xs text-gray-500">Auto-load cohort into composer</div>
                <button
                  className="w-full text-left px-3 py-2 hover:bg-gray-50"
                  onClick={async()=>{
                    try {
                      const res = await fetchCohort('leads_incomplete_signup');
                      const items = Array.isArray((res as any)?.items) ? (res as any).items : [];
                      const phones: string[] = Array.from(new Set<string>(items.map((l:any)=> String(l.phoneNumber||'').trim()).filter(Boolean)));
                      if (phones.length === 0) { safeToast('info','No phone numbers for Incomplete Signup cohort'); setShowIncompleteMenu(false); return; }
                      setLeads(items as any);
                      setCohortsLoaded(prev => ({ ...prev, incomplete: true }));
                      const linkTpl = `${window.location.origin.replace(/\/$/, '')}/?complete-signup=1&email={{email}}`;
                      setSmsPreset({
                        recipients: phones,
                        title: 'Complete your signup',
                        message: `You started your TCard. Finish signup: ${linkTpl}`,
                        group: 'non_users'
                      });
                      setShowIncompleteMenu(false);
                      setTab('sms');
                    } catch {
                      safeToast('error','Failed to load Incomplete Signup cohort');
                      setShowIncompleteMenu(false);
                    }
                  }}
                >
                  SMS…
                </button>
                <button
                  className="w-full text-left px-3 py-2 hover:bg-gray-50"
                  onClick={async()=>{
                    try {
                      const res = await fetchCohort('leads_incomplete_signup');
                      const items = Array.isArray((res as any)?.items) ? (res as any).items : [];
                      const emails: string[] = Array.from(new Set<string>(items.map((l:any)=> String(l.email||'').trim()).filter(Boolean)));
                      if (emails.length === 0) { safeToast('info','No emails for Incomplete Signup cohort'); setShowIncompleteMenu(false); return; }
                      setLeads(items as any);
                      setCohortsLoaded(prev => ({ ...prev, incomplete: true }));
                      const linkTpl = `${window.location.origin.replace(/\/$/, '')}/?complete-signup=1&email={{email}}`;
                      setEmailPreset({
                        recipients: emails,
                        subject: 'Finish setting up your TCard',
                        message: `Hello! You started creating your TCard. Complete your signup to go live: ${linkTpl}`,
                        group: 'non_users'
                      });
                      setShowIncompleteMenu(false);
                      setTab('email');
                    } catch {
                      safeToast('error','Failed to load Incomplete Signup cohort');
                      setShowIncompleteMenu(false);
                    }
                  }}
                >
                  Email…
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {showExportConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded p-4 w-96">
            <h4 className="text-lg font-semibold mb-2">Confirm export</h4>
            <p className="text-sm text-gray-600 mb-4">Export {exportPendingIds?.length || 0} selected user(s) to CSV? This will download a file to your browser.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowExportConfirm(false); setExportPendingIds(null); }} className="px-3 py-1 border rounded">Cancel</button>
              <button onClick={() => { exportUsersCSV(exportPendingIds || []); setShowExportConfirm(false); setExportPendingIds(null); }} className="px-3 py-1 bg-blue-600 text-white rounded">Export</button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4">
        {/* Tabs — horizontal scroll on mobile */}
        <div className="flex overflow-x-auto gap-2 sm:gap-4 mb-2 pb-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          {['overview'].map(t => (
            <button key={t} onClick={() => setTab(t as any)} className={`px-3 py-2 text-sm rounded whitespace-nowrap shrink-0 ${tab===t ? 'bg-slate-800 text-white' : 'bg-white border'}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
          ))}
        </div>
        <div className="flex overflow-x-auto gap-2 sm:gap-4 mb-4 pb-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          {['stats','sms','email','users','ads','subscriptions','templates','email_templates'].map(t => (
            <button key={t} onClick={() => setTab(t as any)} className={`px-3 py-2 text-sm rounded whitespace-nowrap shrink-0 ${tab===t ? 'bg-slate-800 text-white' : 'bg-white border'}`}>{t === 'email_templates' ? 'Email Tpl' : t.charAt(0).toUpperCase() + t.slice(1)}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        <div className="lg:col-span-1">
          <div className={`bg-white rounded-lg p-3 sm:p-4 border ${(
            tab === 'stats' ||
            tab === 'ads' ||
            tab === 'sms' ||
            tab === 'email' ||
            tab === 'users' ||
            tab === 'subscriptions' ||
            tab === 'templates' ||
            tab === 'email_templates'
          ) ? 'overflow-visible' : 'max-h-[72vh] overflow-auto'}`}>
            {tab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  <div className="p-6 rounded-lg border bg-blue-50 border-blue-200 hover:shadow-md transition-shadow h-full flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-600 text-sm font-medium">Total Users</p>
                        <p className="text-2xl font-bold mt-1 text-blue-900">{displayedTotalUsers}</p>
                      </div>
                      <Users className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-500"><div>vs last period</div><div className="text-green-500 font-medium">+{Math.floor(Math.random()*12)}%</div></div>
                  </div>

                  <div className="p-6 rounded-lg border bg-green-50 border-green-200 hover:shadow-md transition-shadow h-full flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-600 text-sm font-medium">Active Users</p>
                        <p className="text-2xl font-bold mt-1 text-green-900">{displayedActiveUsers}</p>
                      </div>
                      <Eye className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-500"><div>vs last period</div><div className="text-green-500 font-medium">+{Math.floor(Math.random()*12)}%</div></div>
                  </div>

      <div className="p-6 rounded-lg border bg-purple-50 border-purple-200 hover:shadow-md transition-shadow h-full flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-600 text-sm font-medium">Total Views</p>
        <p className="text-2xl font-bold mt-1 text-purple-900">{Number(totalViews || 0).toLocaleString()}</p>
                      </div>
                      <BarChart3 className="w-8 h-8 text-purple-600" />
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-500"><div>vs last period</div><div className="text-green-500 font-medium">+{Math.floor(Math.random()*12)}%</div></div>
                  </div>

                  
                  {/* Restored Total Shares card */}
                  <div className="p-6 rounded-lg border bg-amber-50 border-amber-200 hover:shadow-md transition-shadow h-full flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-600 text-sm font-medium">Total Shares</p>
                        <p className="text-2xl font-bold mt-1 text-amber-900">{Number(totalShares || 0).toLocaleString()}</p>
                      </div>
                      <Share2 className="w-8 h-8 text-amber-600" />
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-500"><div>vs last period</div><div className="text-green-500 font-medium">+{Math.floor(Math.random()*12)}%</div></div>
                  </div>

                  {/* New: Call Clicks */}
                  <div className="p-6 rounded-lg border bg-red-50 border-red-200 hover:shadow-md transition-shadow h-full flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-red-600 text-sm font-medium">Call Clicks</p>
                        <p className="text-2xl font-bold mt-1 text-red-900">{Number(totalCallClicks || 0).toLocaleString()}</p>
                      </div>
                      <Phone className="w-8 h-8 text-red-600" />
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-500"><div>vs last period</div><div className="text-green-500 font-medium">+{Math.floor(Math.random()*12)}%</div></div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button onClick={() => setTab('users')} className="flex items-center justify-center p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"><Users className="w-5 h-5 mr-2" />Manage Users</button>
                    <button onClick={() => setTab('ads')} className="flex items-center justify-center p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"><Image className="w-5 h-5 mr-2" />Manage Ads</button>
                    <button onClick={() => setShowCreateModal(true)} className="flex items-center justify-center p-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"><Plus className="w-5 h-5 mr-2" />Create Card User</button>
                    <button
                      onClick={() => { try { window.open('https://72.61.18.114:8090/snappymail/index.php', '_blank', 'noopener'); } catch {} }}
                      className="flex items-center justify-center p-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      title="Open SnappyMail email portal in a new tab"
                    >
                      <Mail className="w-5 h-5 mr-2" />Email Portal
                    </button>
                  </div>
                </div>

              </div>
            )}

            {tab === 'sms' && (<BulkSMSForm leads={leads} onSubmit={submitBulkRequest} preset={smsPreset || undefined} />)}
            {tab === 'email' && (<BulkEmailForm leads={leads} onSubmit={submitBulkRequest} preset={emailPreset || undefined} />)}
            {tab === 'email_templates' && (<EmailTemplatesPanel />)}

            {tab === 'users' && (
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium text-gray-700">{selectedUsers.length} of {users.length} selected</span>
                      <button
                        onClick={() => {
                          if (!managersLoaded || users.length === 0) return;
                          const eligible = users.filter(u => !isSystemAdminCard(u)).map(u => u.id);
                          const allSelected = selectedUsers.length === eligible.length && eligible.length > 0 && eligible.every(id => selectedUsers.includes(String(id)));
                          setSelectedUsers(allSelected ? [] : eligible.map(e => String(e)));
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
                        disabled={!managersLoaded}
                      >{(() => {
                        if (!managersLoaded) return 'Select All';
                        const eligible = users.filter(u => !isSystemAdminCard(u)).map(u => u.id);
                        const allSelected = selectedUsers.length === eligible.length && eligible.length > 0 && eligible.every(id => selectedUsers.includes(String(id)));
                        return allSelected ? 'Deselect All' : 'Select All';
                      })()}</button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => {
                        if (selectedUsers.length===0) return safeToast('info','No users selected');
                        const blocked = users.filter(u => selectedUsers.includes(getId(u)) && isSystemAdminCard(u)).length;
                        setUsers(prev => prev.map(u => (selectedUsers.includes(getId(u)) && !isSystemAdminCard(u)) ? { ...u, isActive: true } : u));
                        if (blocked > 0) safeToast('info', `Skipped ${blocked} System Admin card(s)`);
                        const affected = selectedUsers.length - blocked;
                        if (affected > 0) safeToast('success', `Activated ${affected} users`);
                        setSelectedUsers([]);
                      }} className="px-3 py-1 bg-green-600 text-white rounded text-sm disabled:opacity-50" disabled={!managersLoaded}>Activate</button>
                      <button onClick={() => {
                        if (selectedUsers.length===0) return safeToast('info','No users selected');
                        const blocked = users.filter(u => selectedUsers.includes(getId(u)) && isSystemAdminCard(u)).length;
                        setUsers(prev => prev.map(u => (selectedUsers.includes(getId(u)) && !isSystemAdminCard(u)) ? { ...u, isActive: false } : u));
                        if (blocked > 0) safeToast('info', `Skipped ${blocked} System Admin card(s)`);
                        const affected = selectedUsers.length - blocked;
                        if (affected > 0) safeToast('success', `Deactivated ${affected} users`);
                        setSelectedUsers([]);
                      }} className="px-3 py-1 bg-yellow-600 text-white rounded text-sm disabled:opacity-50" disabled={!managersLoaded}>Deactivate</button>
                      <select value={bulkTemplate} onChange={(e)=> setBulkTemplate(e.target.value)} className="px-2 py-1 border rounded text-sm">{templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
                      <button onClick={() => { if (selectedUsers.length===0) return safeToast('info','No users selected'); setUsers(prev => prev.map(u => selectedUsers.includes(getId(u)) ? { ...u, templateId: bulkTemplate, templateMetaId: undefined } : u)); safeToast('success', `Applied template to ${selectedUsers.length} users`); setSelectedUsers([]); }} className="px-3 py-1 bg-purple-600 text-white rounded text-sm">Apply Template</button>
                      <button onClick={() => { if (selectedUsers.length===0) return safeToast('info','No users selected'); setExportPendingIds(selectedUsers); setShowExportConfirm(true); }} className={`px-3 py-1 border rounded text-sm ${selectedUsers.length===0 ? 'opacity-50 pointer-events-none' : ''}`}>Export Selected</button>
                      <button onClick={() => setShowCreateModal(true)} className="px-3 py-1 bg-gray-900 text-white rounded text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4"/>Create Card User</button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {users.map((user:any) => (
                    <div key={user.id} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-200">
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => {
                                if (!managersLoaded || isSystemAdminCard(user)) return; // block until loaded & for System Admin card
                                // delegate toggle to parent when available
                                handleRowSelect(user.id);
                              }}
                              disabled={!managersLoaded || isSystemAdminCard(user)}
                              title={!managersLoaded ? 'Loading permissions…' : (isSystemAdminCard(user) ? 'Restricted: System Admin card' : '')}
                              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                            />
                            <img
                              src={user?.avatar ? resolveImageSrc(user.avatar) : avatarFallbackUrl(user?.name, user?.username)}
                              alt={user.name}
                              className="w-14 h-14 rounded-xl object-cover border-2 border-gray-100 shadow-sm"
                              onError={(e) => { const img = e.currentTarget as HTMLImageElement; const fb = avatarFallbackUrl(user?.name, user?.username); if (img.src !== fb) img.src = fb; }}
                            />
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-bold ${user.subscription?.type === 'gold' ? 'bg-yellow-100 text-yellow-800' : user.subscription?.type === 'silver' ? 'bg-gray-100 text-gray-800' : 'bg-blue-100 text-blue-800'}`}>
                            {(user.subscription?.type || 'free').toUpperCase()}
                          </div>
                        </div>

                        <div className="mb-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{user.name}</h3>
                          <p className="text-sm font-medium text-gray-700 mb-1">{user.position}</p>
                          <p className="text-sm text-gray-500">{user.company}</p>
                        </div>

                        <div className="space-y-3 mb-6">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-500">Template:</span>
                            <span className="text-sm font-semibold text-gray-900">{'Default'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-500">Views:</span>
                            <span className="text-sm font-bold text-blue-600">{(user.analytics?.views || 0).toLocaleString()}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-500">Saves:</span>
                            <span className="text-sm font-bold text-green-600">{user.analytics?.saves || 0}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-500">Status:</span>
                            <div className={`flex items-center gap-2 ${user.isActive ? 'text-green-600' : 'text-red-600'}`}>
                              <div className={`w-2 h-2 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
                              <span className="text-sm font-medium">{user.isActive ? 'Active' : 'Inactive'}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-3">
                          <button
                            onClick={() => { if (managersLoaded && !isSystemAdminCard(user)) { setCustomizeUserId(user.id); handlePrimarySelect(user.id); } }}
                            disabled={!managersLoaded || isSystemAdminCard(user)}
                            title={!managersLoaded ? 'Loading permissions…' : (isSystemAdminCard(user) ? 'Basic Admin cannot customize System Admin card' : '')}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all duration-200 font-medium shadow-sm ${(!managersLoaded || isSystemAdminCard(user)) ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                          > 
                            <Plus className="w-4 h-4" /> Customize Card
                          </button>

                          {/* Admin inline password reset */}
                          <button
                            onClick={() => resetUserPasswordInline(user)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 font-medium shadow-sm bg-gray-100 hover:bg-gray-200 text-gray-800"
                          >
                            Reset Password
                          </button>

                          <div className="grid grid-cols-2 gap-2">
                            <button onClick={() => { handlePrimarySelect(user.id); safeToast('info','View as Owner (dev)'); }} className="flex items-center justify-center gap-1 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium">View as Owner</button>
                            <button
                              onClick={() => {
                                handlePrimarySelect(user.id);
                                const key = String(user.id || user.username);
                                const url = canonicalCardUrl(user.handle || user.username || key, String(user.username || key));
                                window.open(url, '_blank');
                              }}
                              className="flex items-center justify-center gap-1 px-3 py-2 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                            >
                              Preview Card
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* customization modal handled once below; duplicate removed here */}

              </div>
            )}

            {tab === 'ads' && (
              <div className="space-y-6">
                {/* Create New Advertisement (replicated from SystemAdmin, delete removed for Basic Admin) */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Advertisement</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder="Advertisement Title"
                      value={(newAd as any)?.title || ''}
                      onChange={(e) => setNewAd({ ...(newAd as any), title: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={(newAd as any)?.description || ''}
                      onChange={(e) => setNewAd({ ...(newAd as any), description: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="url"
                      placeholder="Link URL"
                      value={(newAd as any)?.link || ''}
                      onChange={(e) => setNewAd({ ...(newAd as any), link: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (!file) return;
                          // temporary local preview
                          const temp = URL.createObjectURL(file);
                          setNewAd({ ...(newAd as any), image: temp });
                          try {
                            const form = new FormData();
                            form.append('image', file);
                            const token = localStorage.getItem('dev_admin_token');
                            const res = await fetch('/api/admin/uploads/image', {
                              method: 'POST',
                              body: form,
                              headers: token ? { Authorization: `Bearer ${token}` } : undefined as any,
                              credentials: 'include',
                            });
                            if (res.ok) {
                              const data = await res.json();
                              if (data?.url) setNewAd({ ...(newAd as any), image: data.url });
                            }
                          } catch {}
                          finally {
                            try { URL.revokeObjectURL(temp); } catch {}
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      {(newAd as any)?.image && (
                        <div className="mt-2">
                          <img src={(newAd as any).image} alt="Ad preview" className="w-20 h-20 object-cover rounded border" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Targeting */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Age Range</label>
                      <select
                        multiple
                        value={(newAd as any)?.targetAudience?.ageRange || []}
                        onChange={(e) => {
                          const values = Array.from(e.target.selectedOptions, (option) => option.value);
                          setNewAd({ ...(newAd as any), targetAudience: { ageRange: values, gender: (newAd as any)?.targetAudience?.gender || [], location: (newAd as any)?.targetAudience?.location || [], jobTitles: (newAd as any)?.targetAudience?.jobTitles || [] } });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="18-25">18-25</option>
                        <option value="25-35">25-35</option>
                        <option value="35-45">35-45</option>
                        <option value="45-55">45-55</option>
                        <option value="55+">55+</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                      <select
                        multiple
                        value={(newAd as any)?.targetAudience?.gender || []}
                        onChange={(e) => {
                          const values = Array.from(e.target.selectedOptions, (option) => option.value);
                          setNewAd({ ...(newAd as any), targetAudience: { ageRange: (newAd as any)?.targetAudience?.ageRange || [], gender: values, location: (newAd as any)?.targetAudience?.location || [], jobTitles: (newAd as any)?.targetAudience?.jobTitles || [] } });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                        <option value="all">All</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Locations</label>
                      <select
                        multiple
                        value={(newAd as any)?.targetAudience?.location || []}
                        onChange={(e) => {
                          const values = Array.from(e.target.selectedOptions, (option) => option.value);
                          setNewAd({ ...(newAd as any), targetAudience: { ageRange: (newAd as any)?.targetAudience?.ageRange || [], gender: (newAd as any)?.targetAudience?.gender || [], location: values, jobTitles: (newAd as any)?.targetAudience?.jobTitles || [] } });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg h-32"
                      >
                        <option value="all">All Locations</option>
                        {getUniqueLocations().map((location) => (
                          <option key={location} value={location}>{location}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple locations</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Job Titles</label>
                      <select
                        multiple
                        value={(newAd as any)?.targetAudience?.jobTitles || []}
                        onChange={(e) => {
                          const values = Array.from(e.target.selectedOptions, (option) => option.value);
                          setNewAd({ ...(newAd as any), targetAudience: { ageRange: (newAd as any)?.targetAudience?.ageRange || [], gender: (newAd as any)?.targetAudience?.gender || [], location: (newAd as any)?.targetAudience?.location || [], jobTitles: values } });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        {getUniqueJobTitles().map((title) => (
                          <option key={title} value={title}>{title}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Scheduling */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={(newAd as any)?.schedule?.startDate || ''}
                        onChange={(e) => setNewAd({ ...(newAd as any), schedule: { startDate: e.target.value, endDate: (newAd as any)?.schedule?.endDate || '', startTime: (newAd as any)?.schedule?.startTime || '09:00', endTime: (newAd as any)?.schedule?.endTime || '18:00', timezone: (newAd as any)?.schedule?.timezone || 'UTC' } })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={(newAd as any)?.schedule?.endDate || ''}
                        onChange={(e) => setNewAd({ ...(newAd as any), schedule: { startDate: (newAd as any)?.schedule?.startDate || '', endDate: e.target.value, startTime: (newAd as any)?.schedule?.startTime || '09:00', endTime: (newAd as any)?.schedule?.endTime || '18:00', timezone: (newAd as any)?.schedule?.timezone || 'UTC' } })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                      <input
                        type="time"
                        value={(newAd as any)?.schedule?.startTime || '09:00'}
                        onChange={(e) => setNewAd({ ...(newAd as any), schedule: { startDate: (newAd as any)?.schedule?.startDate || '', endDate: (newAd as any)?.schedule?.endDate || '', startTime: e.target.value, endTime: (newAd as any)?.schedule?.endTime || '18:00', timezone: (newAd as any)?.schedule?.timezone || 'UTC' } })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                      <input
                        type="time"
                        value={(newAd as any)?.schedule?.endTime || '18:00'}
                        onChange={(e) => setNewAd({ ...(newAd as any), schedule: { startDate: (newAd as any)?.schedule?.startDate || '', endDate: (newAd as any)?.schedule?.endDate || '', startTime: (newAd as any)?.schedule?.startTime || '09:00', endTime: e.target.value, timezone: (newAd as any)?.schedule?.timezone || 'UTC' } })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        handleAddAd();
                      }}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Advertisement
                    </button>
                    <button onClick={() => setNewAd(initialNewAd())} className="px-3 py-2 border rounded">Reset</button>
                  </div>
                </div>

                {/* Existing Advertisements */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Existing Advertisements</h3>
                  <div className="bg-gray-50 border rounded-lg p-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Title</label>
                        <input value={adFilterTitle} onChange={(e)=> setAdFilterTitle(e.target.value)} placeholder="Search title" className="w-full px-2 py-1 border rounded" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Type</label>
                        <select value={adFilterType} onChange={(e)=> setAdFilterType(e.target.value as any)} className="w-full px-2 py-1 border rounded">
                          <option value="all">All</option>
                          <option value="free">Free</option>
                          <option value="paid">Paid</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Created On</label>
                        <input type="date" value={adFilterCreatedOn} onChange={(e)=> setAdFilterCreatedOn(e.target.value)} className="w-full px-2 py-1 border rounded" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Ended On</label>
                        <input type="date" value={adFilterEndedOn} onChange={(e)=> setAdFilterEndedOn(e.target.value)} className="w-full px-2 py-1 border rounded" />
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button onClick={() => { setAdFilterTitle(''); setAdFilterType('all'); setAdFilterCreatedOn(''); setAdFilterEndedOn(''); }} className="px-3 py-1 border rounded text-sm">Clear Filters</button>
                      <button onClick={() => downloadAdReportCSV(filteredAds)} className="px-3 py-1 border rounded text-sm">Export CSV</button>
                      <button onClick={() => downloadAdReportPDF(filteredAds)} className="px-3 py-1 border rounded text-sm">Export PDF</button>
                      <span className="ml-auto text-xs text-gray-500 self-center">{filteredAds.length} shown</span>
                    </div>
                  </div>
                  {filteredAds.map((ad) => {
                    const views = Number(((ad as any)?.views ?? (ad as any)?.analytics?.views) || 0);
                    const clicks = Number((ad as any)?.clicks || 0);
                    const ctr = views > 0 ? ((clicks / views) * 100).toFixed(1) : '0.0';
                    return (
                      <div key={ad.id} className="border rounded-lg p-4">
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="bg-blue-50 p-3 rounded-lg text-center">
                            <p className="text-2xl font-bold text-blue-600">{views.toLocaleString()}</p>
                            <p className="text-xs text-blue-800 font-medium">Views</p>
                          </div>
                          <div className="bg-green-50 p-3 rounded-lg text-center">
                            <p className="text-2xl font-bold text-green-600">{clicks}</p>
                            <p className="text-xs text-green-800 font-medium">Clicks</p>
                          </div>
                          <div className="bg-purple-50 p-3 rounded-lg text-center">
                            <p className="text-2xl font-bold text-purple-600">{ctr}%</p>
                            <p className="text-xs text-purple-800 font-medium">CTR</p>
                          </div>
                        </div>

                        <div className="flex items-start justify-between">
                          <div className="flex space-x-4 flex-1">
                            <img src={ad.image} alt={ad.title} className="w-16 h-16 object-cover rounded" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{ad.title}</h4>
                              <p className="text-sm text-gray-600 mb-2">{ad.description}</p>

                              <div className="text-xs text-gray-500 mb-2">
                                <div className="flex items-center gap-4">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {ad.schedule?.startDate} - {ad.schedule?.endDate}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {ad.schedule?.startTime} - {ad.schedule?.endTime} {ad.schedule?.timezone}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <span>Ages: {(ad.targetAudience?.ageRange || []).join(', ')}</span>
                                <span>Gender: {(ad.targetAudience?.gender || []).join(', ')}</span>
                                <span>
                                  Locations: {(ad.targetAudience?.location || []).slice(0, 2).join(', ')}
                                  {(ad.targetAudience?.location || []).length > 2 ? ` +${(ad.targetAudience?.location || []).length - 2} more` : ''}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => { toggleAdActive(getId(ad)); safeToast('success', `Advertisement ${ad.isActive ? 'deactivated' : 'activated'} successfully`); }}
                              className={`p-2 rounded ${ad.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                              title={ad.isActive ? 'Deactivate' : 'Activate'}
                            >
                              {ad.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => { setEditingAd(ad); setAdModalOpen(true); }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit Advertisement"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 21v-3a4 4 0 0 1 4-4h3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                            <button
                              onClick={() => downloadSingleAdPDF(ad)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                              title="Download Report"
                            >
                              <BarChart3 className="w-4 h-4" />
                            </button>
                            {/* Delete omitted for Basic Admin - only Super Admin can delete in System Admin UI */}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tab === 'subscriptions' && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6 rounded-lg border border-yellow-200">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-yellow-900 mb-2 flex items-center">
                        <Plus className="w-6 h-6 mr-2" />
                        Subscription Plans Management
                      </h3>
                      <p className="text-gray-600">Manage pricing plans and subscription features</p>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => setEditingSubscriptionPlans(!editingSubscriptionPlans)} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors">
                        {editingSubscriptionPlans ? 'Stop Editing' : 'Edit Plans'}
                      </button>
                      <button onClick={() => addSubscriptionPlan({ name: 'New Plan', price: '$0', monthlyPrice: 0, description: 'New subscription plan', features: ['Feature 1'], buttonText: 'Get Started', buttonColor: 'bg-blue-600 hover:bg-blue-700', isPopular: false, isActive: true })} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                        <Plus className="w-4 h-4" />
                        Add Plan
                      </button>
                      <button onClick={() => { localStorage.removeItem('basic_admin_homepage'); setHomepageSettings({}); safeToast('success','Local preview reset'); recordActivity('Reset local preview'); }} className="px-3 py-2 border rounded text-sm">Reset Local Preview</button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
                    {plansList.map((plan:any) => (
                      <div key={plan.id} className={`bg-white rounded-xl border-2 ${plan.isPopular ? 'border-yellow-400' : 'border-gray-200'} relative flex flex-col`}>
                        <div className="p-6 flex-1 flex flex-col min-h-[380px] md:min-h-[480px] lg:min-h-[520px]">
                        {plan.isPopular && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2"><div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold">Most Popular</div></div>}

                        <div className="mb-4">
                          {editingSubscriptionPlans ? (
                            <input type="text" value={plan.name} onChange={(e) => updateSubscriptionPlan(plan.id, { name: e.target.value })} className="w-full text-xl font-bold text-gray-900 bg-transparent border-b border-gray-300 focus:border-blue-500 outline-none" />
                          ) : (
                            <h4 className="text-xl font-bold text-gray-900">{plan.name}</h4>
                          )}
                        </div>

                        <div className="mb-4">
                          {editingSubscriptionPlans ? (
                            <input type="text" value={plan.price} onChange={(e) => updateSubscriptionPlan(plan.id, { price: e.target.value })} className="text-3xl font-bold text-gray-900 bg-transparent border-b border-gray-300 focus:border-blue-500 outline-none w-full" />
                          ) : (
                            <div className="text-3xl font-bold text-gray-900">{plan.price}</div>
                          )}
                          {plan.price !== 'Free' && <span className="text-lg text-gray-600">/month</span>}
                        </div>

                        <div className="mb-6">
                          {editingSubscriptionPlans ? (
                            <textarea value={plan.description} onChange={(e) => updateSubscriptionPlan(plan.id, { description: e.target.value })} className="w-full text-gray-600 bg-transparent border border-gray-300 rounded p-2 focus:border-blue-500 outline-none resize-none" rows={2} />
                          ) : (
                            <p className="text-gray-600">{plan.description}</p>
                          )}
                        </div>

                        <div className="space-y-3 mb-6">
                          {plan.features.map((feature:string, index:number) => (
                            <div key={index} className="flex items-start gap-3">
                              <div className="w-3 h-3 bg-green-500 mt-1 rounded-full flex-shrink-0" />
                              {editingSubscriptionPlans ? (
                                <input type="text" value={feature} onChange={(e) => { const newFeatures = [...plan.features]; newFeatures[index] = e.target.value; updateSubscriptionPlan(plan.id, { features: newFeatures }); }} className="flex-1 text-gray-700 bg-transparent border-b border-gray-200 focus:border-blue-500 outline-none text-sm" />
                              ) : (
                                <span className="text-gray-700 text-sm">{feature}</span>
                              )}
                            </div>
                          ))}

                          {editingSubscriptionPlans && (
                            <div>
                              <button onClick={() => { const newFeatures = [...plan.features, 'New Feature']; updateSubscriptionPlan(plan.id, { features: newFeatures }); }} className="flex items-center text-blue-600 hover:text-blue-700 text-sm mt-2"><Plus className="w-3 h-3 mr-1" />Add Feature</button>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <input type="checkbox" checked={plan.isPopular} onChange={(e) => updateSubscriptionPlan(plan.id, { isPopular: e.target.checked })} disabled={!editingSubscriptionPlans} className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500" />
                            <span className="text-sm text-gray-700">Popular</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input type="checkbox" checked={plan.isActive} onChange={(e) => updateSubscriptionPlan(plan.id, { isActive: e.target.checked })} disabled={!editingSubscriptionPlans} className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500" />
                            <span className="text-sm text-gray-700">Active</span>
                          </div>
                        </div>

                        <div className="space-y-2 mt-4">
                          {editingSubscriptionPlans ? (
                            <>
                              <input type="text" value={plan.buttonText} onChange={(e) => updateSubscriptionPlan(plan.id, { buttonText: e.target.value })} className="w-full text-center py-2 bg-transparent border border-gray-300 rounded focus:border-blue-500 outline-none font-semibold" placeholder="Button Text" />
                              <select value={plan.buttonColor} onChange={(e) => updateSubscriptionPlan(plan.id, { buttonColor: e.target.value })} className="w-full py-2 border border-gray-300 rounded focus:border-blue-500 outline-none text-sm">
                                <option value="bg-blue-600 hover:bg-blue-700">Blue</option>
                                <option value="bg-green-600 hover:bg-green-700">Green</option>
                                <option value="bg-purple-600 hover:bg-purple-700">Purple</option>
                                <option value="bg-yellow-600 hover:bg-yellow-700">Yellow</option>
                                <option value="bg-red-600 hover:bg-red-700">Red</option>
                                <option value="bg-gray-600 hover:bg-gray-700">Gray</option>
                                <option value="from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700">Gold Gradient</option>
                              </select>

                              <div className="mt-2 flex gap-2">
                                <button onClick={() => { setEditingPlan(plan); setPlanModalOpen(true); }} className="px-3 py-1 border rounded">Edit</button>
                                <button onClick={() => deleteSubscriptionPlan(plan.id)} className={`px-3 py-1 border rounded ${plan.id && plan.id.startsWith('p_') ? 'text-red-600' : 'text-red-200 cursor-not-allowed'}`} disabled={!(plan.id && plan.id.startsWith('p_'))}>Delete</button>
                              </div>
                            </>
                          ) : (
                            <div className="mt-auto">
                              {(() => {
                                const isGradient = typeof plan.buttonColor === 'string' && plan.buttonColor.includes('from-');
                                const btnClass = `w-full py-3 text-white rounded-xl font-semibold ${isGradient ? 'bg-gradient-to-r ' : ''}${plan.buttonColor} shadow-sm`;
                                return <button className={btnClass}>{plan.buttonText}</button>;
                              })()}
                            </div>
                          )}
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Moved: Expired Users panel from Stats tab into Subscriptions tab */}
                <div className="bg-white border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">Expired Users ({filteredExpiredUsers.length})</h4>
                  </div>
                  <div className="flex flex-wrap gap-4 mb-3">
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-600">Email</label>
                      <input value={expiredEmail} onChange={(e)=> { setExpiredEmail(e.target.value); setExpiredPage(1); }} className="px-2 py-1 border rounded text-sm" placeholder="Filter email" />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-600">Username</label>
                      <input value={expiredUsername} onChange={(e)=> { setExpiredUsername(e.target.value); setExpiredPage(1); }} className="px-2 py-1 border rounded text-sm" placeholder="Filter username" />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-600">Plan</label>
                      <select value={expiredPlan} onChange={(e)=> { setExpiredPlan(e.target.value); setExpiredPage(1); }} className="px-2 py-1 border rounded text-sm min-w-0 sm:min-w-[140px]">
                        <option value="all">All</option>
                        {expiredPlanOptions.map(p => (<option key={p} value={p}>{p}</option>))}
                      </select>
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-600">Expired Date</label>
                      <input type="date" value={expiredDate} onChange={(e)=> { setExpiredDate(e.target.value); setExpiredPage(1); }} className="px-2 py-1 border rounded text-sm" />
                    </div>
                  </div>
                  <div className="border rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-2 py-1">Name</th>
                          <th className="text-left px-2 py-1">Email</th>
                          <th className="text-left px-2 py-1">Plan</th>
                          <th className="text-left px-2 py-1">Expired</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expiredSlice.map((u:any)=> (
                          <tr key={u.id} className="border-t">
                            <td className="px-2 py-1">{u.name}</td>
                            <td className="px-2 py-1">{u.email}</td>
                            <td className="px-2 py-1">{u.subscription?.type || '—'}</td>
                            <td className="px-2 py-1">{u.subscription?.expiresAt ? new Date(u.subscription.expiresAt).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                        {filteredExpiredUsers.length===0 && (
                          <tr><td className="px-2 py-2 text-gray-500" colSpan={4}>No expired users</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {filteredExpiredUsers.length > expiredItemsPerPage && (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3 text-sm">
                      <span className="text-gray-600">Page {expiredPageSafe} of {expiredTotalPages}</span>
                      <div className="flex items-center gap-1 flex-wrap">
                        <button onClick={()=> setExpiredPage(p => Math.max(1, p-1))} className="px-2 py-1 border rounded disabled:opacity-50" disabled={expiredPageSafe===1}>Prev</button>
                        {Array.from({ length: expiredTotalPages }, (_,i)=> i+1).map(pg => (
                          <button
                            key={pg}
                            onClick={()=> setExpiredPage(pg)}
                            className={`px-2 py-1 border rounded min-w-[36px] ${pg===expiredPageSafe ? 'bg-blue-600 text-white border-blue-600' : 'bg-white hover:bg-gray-50'}`}
                          >{pg}</button>
                        ))}
                        <button onClick={()=> setExpiredPage(p => Math.min(expiredTotalPages, p+1))} className="px-2 py-1 border rounded disabled:opacity-50" disabled={expiredPageSafe===expiredTotalPages}>Next</button>
                      </div>
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <button disabled={loadingSubs || filteredExpiredUsers.length===0} onClick={async()=>{
                      try {
                        const emails: string[] = Array.from(new Set<string>(
                          filteredExpiredUsers.map((u:any)=> String(u.email||'').trim()).filter(Boolean)
                        ));
                        if (emails.length === 0) { safeToast('info','No emails found for expired users'); return; }
                        const pricingLink = `${window.location.origin.replace(/\/$/, '')}/pricing`;
                        const subject = 'Your TCard subscription has expired';
                        const message = [
                          'Hello,',
                          '',
                          'Your TCard subscription has expired and some premium features are now disabled.',
                          'Renew now to restore full access to advanced templates, analytics, and priority support.',
                          '',
                          `Renew your plan: ${pricingLink}`,
                          '',
                          '— Tcards Digital Business Cards'
                        ].join('\n');
                        await submitBulkRequest({ type: 'email', subject, message, recipients: emails });
                      } catch { safeToast('error','Failed to queue email request'); }
                    }} className={`px-3 py-2 rounded ${expiredUsers.length? 'bg-blue-600 text-white':'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>Email Expired Users</button>
                    <button
                      disabled={loadingSubs || filteredExpiredUsers.length===0}
                      onClick={async () => {
                        const phones: string[] = Array.from(new Set<string>(
                          filteredExpiredUsers
                            .map((u:any) => String(u.phoneNumber || u.phone || '').trim())
                            .filter((p:string) => !!p)
                        ));
                        if (phones.length === 0) { safeToast('info','No phone numbers found for expired users'); return; }
                        try {
                          const link = `${window.location.origin.replace(/\/$/, '')}/pricing`;
                          const message = `Your TCard subscription expired. Renew to restore features: ${link}`;
                          await submitBulkRequest({ type: 'sms', title: 'Subscription expired', message, recipients: phones });
                        } catch {}
                      }}
                      className={`px-3 py-2 rounded ${expiredUsers.length? 'bg-green-600 text-white':'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
                    >SMS Expired Users</button>
                    {/* Quick actions preserved from original panel */}
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetchCohort('leads_incomplete_signup');
                          const items = Array.isArray((res as any)?.items) ? (res as any).items : [];
                          const phones: string[] = Array.from(new Set<string>(items.map((l:any)=> String(l.phoneNumber||'').trim()).filter(Boolean)));
                          if (phones.length === 0) { safeToast('info','No phone numbers for Incomplete Signup cohort'); return; }
                          // Queue immediately for review so it shows in System Admin notifications
                          const linkTpl = `${window.location.origin.replace(/\/$/, '')}/?complete-signup=1&email={{email}}`;
                          const message = `You started your TCard. Finish signup to go live: ${linkTpl}`;
                          // provide mapping so server can personalize {{email}} per phone
                          const phoneToEmail: Record<string,string> = {};
                          for (const it of items) {
                            const ph = String(it?.phoneNumber || '').trim();
                            const em = String(it?.email || '').trim();
                            if (ph && em) phoneToEmail[ph] = em;
                          }
                          await submitBulkRequest({ type: 'sms', title: 'Complete your signup', message, recipients: phones, audience: { key: 'leads_incomplete_signup' }, meta: { phoneToEmail } });
                        } catch {
                          safeToast('error','Failed to load Incomplete Signup cohort');
                        }
                      }}
                      className="px-3 py-2 rounded bg-orange-500 text-white"
                    >Send to Incomplete Signup (SMS)</button>
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetchCohort('leads_incomplete_signup');
                          const items = Array.isArray((res as any)?.items) ? (res as any).items : [];
                          const emails: string[] = Array.from(new Set<string>(items.map((l:any)=> String(l.email||'').trim()).filter(Boolean)));
                          if (emails.length === 0) { safeToast('info','No emails for Incomplete Signup cohort'); return; }
                          // Queue immediately for review so it shows in System Admin notifications
                          const linkTpl = `${window.location.origin.replace(/\/$/, '')}/?complete-signup=1&email={{email}}`;
                          const subject = 'Finish setting up your TCard';
                          const message = [
                            'Hello,',
                            '',
                            'It looks like you started creating your TCard but didn\'t finish.',
                            'Complete your signup now to publish your digital business card and start sharing it instantly.',
                            '',
                            `Resume signup: ${linkTpl}`,
                            '',
                            '— Tcards Digital Business Cards'
                          ].join('\n');
                          await submitBulkRequest({ type: 'email', subject, message, recipients: emails });
                        } catch {
                          safeToast('error','Failed to load Incomplete Signup cohort');
                        }
                      }}
                      className="px-3 py-2 rounded bg-amber-500 text-white"
                    >Send to Incomplete Signup (Email)</button>
                  </div>
                </div>
              </div>
            )}

            {tab === 'templates' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Templates</h2>
                    <p className="text-sm text-gray-600">Basic Admin can upload templates (.tcard or .zip) and submit for Super Admin approval. You can submit only your uploads; approved system templates are view-only.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600">Approval</label>
                    <select value={tplFilterApproval} onChange={(e)=> setTplFilterApproval(e.target.value as any)} className="text-sm border rounded px-2 py-1">
                      <option value="all">All</option>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <button onClick={async()=>{ setTplLoading(true); try { setServerTemplates(await listServerTemplates({})); } catch(e:any){ safeToast('error', e?.message||'Refresh failed'); } finally { setTplLoading(false); } }} className="px-3 py-2 border rounded">Refresh</button>
                  </div>
                </div>
                {/* Upload new template (mirrors SystemAdminPanel) */}
                <form onSubmit={handleTplSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input value={tplName} onChange={(e)=> setTplName(e.target.value)} required className="w-full px-3 py-2 border rounded-lg" placeholder="Elegant Looking" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select value={tplCategory} onChange={(e)=> setTplCategory(e.target.value as any)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="business">Business</option>
                      <option value="artist">Artist</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preview Image URL</label>
                    <input value={tplPreviewUrl} onChange={(e)=> setTplPreviewUrl(e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="/uploads/previews/template2.png" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input value={tplDesc} onChange={(e)=> setTplDesc(e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Short description" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Upload Template (.tcard)</label>
                    <input type="file" accept=".tcard" onChange={(e)=> { const f = e.currentTarget.files?.[0]||null; setTplTCardFile(f); loadTplPreview(f); }} className="w-full px-3 py-2 border rounded-lg" />
                    <p className="text-xs text-gray-500 mt-1">.tcard should include meta.json with name, description, category, componentKey and preview image.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">OR Upload Bundle (.zip)</label>
                    <input type="file" accept=".zip" onChange={(e)=> setTplBundleFile(e.currentTarget.files?.[0]||null)} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div className="md:col-span-3">
                    {(tplPreviewMeta || tplTCardFile) && (
                      <div className="mt-2 border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-semibold">Template Preview</div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={()=> loadTplPreview(tplTCardFile)} className="text-xs px-2 py-1 rounded border bg-white">{tplPreviewLoading ? 'Refreshing…' : 'Refresh Preview'}</button>
                            <button type="button" onClick={()=> { setTplPreviewMeta(null); setTplTCardFile(null); }} className="text-xs px-2 py-1 rounded border bg-white">Clear</button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            {tplPreviewMeta?.previewUrl ? <img src={tplPreviewMeta.previewUrl as any} alt="template preview" className="w-full rounded border" /> : <div className="text-xs text-gray-500">No preview image extracted.</div>}
                            {tplPreviewMeta?.name && (<div className="text-sm text-gray-700"><span className="font-medium">Name:</span> {tplPreviewMeta?.name as any}</div>)}
                            {tplPreviewMeta?.description && (<div className="text-sm text-gray-700"><span className="font-medium">Description:</span> {tplPreviewMeta?.description as any}</div>)}
                            <div className="text-sm text-gray-700"><span className="font-medium">Category:</span> {(tplPreviewMeta?.category as any) || tplCategory}</div>
                          </div>
                          <div>
                            {(() => {
                              const isDynamic = !!(tplPreviewMeta && (((typeof (tplPreviewMeta as any).renderer === 'string' && (tplPreviewMeta as any).renderer.startsWith('dynamic')) || (tplPreviewMeta as any).spec)));
                              if (tplPreviewLoading && !tplPreviewMeta) {
                                return (
                                  <div className="w-full flex items-center justify-center">
                                    <div className="w-full max-w-[150px] md:max-w-[200px] lg:max-w-[232px] h-[280px] rounded-2xl border bg-white shadow-sm grid place-items-center text-xs text-gray-500">
                                      Generating live preview…
                                    </div>
                                  </div>
                                );
                              }
                              if (isDynamic && tplPreviewMeta) {
                                return (
                                  <div className="flex items-center justify-center">
                                    <div className="w-full max-w-[280px] md:max-w-[320px] lg:max-w-[360px]">
                                      <MetaPreviewCard
                                        template={{
                                          id: 'preview',
                                          name: (tplPreviewMeta as any)?.name || tplName || 'Preview Template',
                                          description: (tplPreviewMeta as any)?.description,
                                          category: ((tplPreviewMeta as any)?.category as any) === 'artist' ? 'Artist' : 'Business',
                                          renderer: ((tplPreviewMeta as any).renderer as any) || 'dynamic-v1',
                                          componentKey: String((tplPreviewMeta as any).componentKey || ''),
                                          spec: (tplPreviewMeta as any).spec,
                                          coverUrl: (tplPreviewMeta as any).previewUrl,
                                          assets: (tplPreviewMeta as any).assets,
                                          version: (tplPreviewMeta as any).version,
                                          status: 'uploaded',
                                        } as any}
                                        user={{
                                          name: tplName || (tplPreviewMeta as any)?.name || 'Preview User',
                                          position: 'Sample Role',
                                          company: 'Sample Company',
                                          email: 'sample@example.com',
                                          phone: '+000 000 000',
                                          avatarUrl: '',
                                          coverUrl: (tplPreviewMeta as any)?.previewUrl || tplPreviewUrl,
                                        }}
                                        // keep theme colors when provided
                                        accent={((tplPreviewMeta as any)?.spec?.theme?.primary || (tplPreviewMeta as any)?.spec?.colors?.primary) ? undefined : "#6366F1"}
                                        mode="grid"
                                        className="mx-auto"
                                      />
                                    </div>
                                  </div>
                                );
                              }
                              if (tplPreviewMeta) {
                                const variantKey = ensureTemplateVariant(
                                  (tplPreviewMeta as any)?.componentKey,
                                  (tplPreviewMeta as any)?.variant
                                );
                                return (
                                  <PreviewCard
                                    variant={resolveVariant(variantKey || (tplPreviewMeta as any)?.componentKey || 'professional-classic')}
                                    user={{
                                      name: tplName || (tplPreviewMeta as any)?.name || 'Preview User',
                                      position: 'Sample Role',
                                      company: 'Sample Company',
                                      coverUrl: (tplPreviewMeta as any)?.previewUrl || tplPreviewUrl,
                                    }}
                                    mode="grid"
                                    templateMeta={{ componentKey: (tplPreviewMeta as any)?.componentKey, variant: (tplPreviewMeta as any)?.variant }}
                                    templateSpec={(tplPreviewMeta as any)?.spec || null}
                                  />
                                );
                              }
                              return (
                                <div className="w-full h-28 rounded-2xl border bg-gray-50 grid place-items-center text-xs text-gray-400">Preview</div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <button type="submit" disabled={tplSubmitting || !tplName || (!!tplTCardFile && tplPreviewLoading)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{tplSubmitting ? 'Uploading…' : 'Add Template'}</button>
                  </div>
                </form>
                {tplLoading ? (
                  <div className="text-sm text-gray-500">Loading templates…</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* visibility: show all approved system templates + the ones uploaded by this admin (pending/rejected included) */}
                    {visibleServerTemplates.map((t) => (
                      <div key={t.id} className="border rounded-lg bg-white overflow-hidden">
                        <div className="h-28 bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                          {t.previewUrl ? <img src={t.previewUrl} alt={t.name} className="w-full h-full object-cover" /> : 'No preview'}
                        </div>
                        <div className="p-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-semibold text-gray-900">{t.name}</div>
                              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <span className={`px-2 py-0.5 rounded-full ${t.category==='artist'?'bg-pink-50 text-pink-700':'bg-indigo-50 text-indigo-700'}`}>{t.category}</span>
                                {t.approvalStatus && (
                                  <span className={`px-2 py-0.5 rounded-full ${t.approvalStatus==='approved'?'bg-green-50 text-green-700': t.approvalStatus==='pending'?'bg-yellow-50 text-yellow-700':'bg-red-50 text-red-700'}`}>{t.approvalStatus}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {(() => {
                                const email = String(currentUser?.email||'').toLowerCase();
                                const uid = (currentUser as any)?.id || (currentUser as any)?._id;
                                const own = (String((t as any).submittedByEmail||'').toLowerCase()===email) || ((t as any).submittedById && uid && String((t as any).submittedById)===String(uid));
                                if (!own) {
                                  return (
                                    <button
                                      onClick={()=> { setPeekTplId(t.id); }}
                                      className="px-2 py-1 rounded text-xs border"
                                    >View</button>
                                  );
                                }
                                const disabled = (t.approvalStatus==='approved' || t.approvalStatus==='pending');
                                return (
                                  <button
                                    onClick={async()=>{ try { const updated = await submitTemplateForApproval(t.id); setServerTemplates(prev => prev.map(x => x.id===t.id?updated:x)); } catch(e:any){ safeToast('error', e?.message || 'Failed to submit'); } }}
                                    disabled={disabled}
                                    className={`px-2 py-1 rounded text-xs ${disabled?'bg-gray-200 text-gray-600':'bg-blue-600 text-white'}`}
                                  >{t.approvalStatus==='pending'?'Pending…':'Submit'}</button>
                                );
                              })()}
                            </div>
                          </div>
                          {t.description && <div className="text-sm text-gray-700 mt-2 line-clamp-2">{t.description}</div>}
                        </div>
                      </div>
                    ))}
                    {serverTemplates.length===0 && (
                      <div className="text-sm text-gray-500">No templates yet.</div>
                    )}
                    {/* Modal preview for selected template */}
                    <TemplatePreviewDialog open={!!peekTplId} onClose={()=> setPeekTplId(null)} title={serverTemplates.find(x => x.id===peekTplId)?.name || 'Preview'}>
                      {(() => {
                        const t = serverTemplates.find(x => x.id===peekTplId);
                        const isDyn = (typeof (t as any)?.renderer === 'string' && (t as any)?.renderer.startsWith('dynamic')) || !!(t as any)?.spec;
                        if (t && isDyn) {
                          return (
                            <MetaPreviewCard
                              template={{
                                id: t.id,
                                name: t.name,
                                description: t.description,
                                category: (t.category as any) === 'artist' ? 'Artist' : 'Business',
                                renderer: ((t as any).renderer as any) || 'dynamic-v1',
                                componentKey: String(t.componentKey || ''),
                                spec: (t as any).spec,
                                coverUrl: t.previewUrl,
                                assets: (t as any).assets,
                                version: (t as any).version,
                                status: ((t as any).status as any) || (t.active ? 'active' : 'uploaded'),
                              } as any}
                              user={{
                                name: 'Preview User',
                                position: 'Sample Role',
                                company: 'Sample Company',
                                email: 'sample@example.com',
                                phone: '+000 000 000',
                                avatarUrl: '',
                                coverUrl: t.previewUrl,
                              }}
                              mode="full"
                              frame="none"
                              allowInnerScroll={true}
                            />
                          );
                        }
                        const variantKey = ensureTemplateVariant(t?.componentKey as any, (t as any)?.variant);
                        return (
                          <PreviewCard
                            variant={resolveVariant(variantKey || t?.componentKey || 'professional-classic')}
                            user={{
                              name: 'Preview User',
                              position: 'Sample Role',
                              company: 'Sample Company',
                              email: 'sample@example.com',
                              phone: '+000 000 000',
                              avatarUrl: '',
                              coverUrl: t?.previewUrl,
                            }}
                            mode="full"
                            templateMeta={{ componentKey: String(t?.componentKey || ''), variant: (t as any)?.variant }}
                            templateSpec={(t as any)?.spec || null}
                          />
                        );
                      })()}
                    </TemplatePreviewDialog>
                  </div>
                )}
              </div>
            )}

            {tab === 'stats' && (
              <div>
                <h2 className="text-lg font-semibold">Statistics</h2>
                <p className="text-sm text-gray-600">Download basic or comprehensive stats (excluding revenue)</p>
                {/* Stats-only quick metrics */}
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border bg-orange-50 border-orange-200 hover:shadow-sm transition-shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-600 text-sm font-medium">Ad Views</p>
                        <p className="text-2xl font-bold mt-1 text-orange-900">{Number(adViews || 0).toLocaleString()}</p>
                      </div>
                      <Eye className="w-7 h-7 text-orange-600" />
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <AdvancedAnalyticsPanel
                    users={users}
                    systemStats={{
                      // Prefer server stats where present, else derive from local
                      totalUsers: Number((serverSystemStats as any)?.totalUsers ?? users.length) || users.length,
                      activeUsers: Number((serverSystemStats as any)?.activeUsers ?? users.filter(u=>u.isActive).length) || users.filter(u=>u.isActive).length,
                      newUsersToday: Number((serverSystemStats as any)?.newUsersToday ?? 0) || 0,
                      totalCards: Number((serverSystemStats as any)?.totalCards ?? users.length) || users.length,
                      activeCards: Number((serverSystemStats as any)?.activeCards ?? users.filter(u=>u.isActive).length) || users.filter(u=>u.isActive).length,
                      totalSubscriptions: Number((serverSystemStats as any)?.totalSubscriptions ?? plansList.length) || plansList.length,
                      silverSubscriptions: Number((serverSystemStats as any)?.silverSubscriptions ?? 0) || 0,
                      goldSubscriptions: Number((serverSystemStats as any)?.goldSubscriptions ?? 0) || 0,
                      trialUsers: Number((serverSystemStats as any)?.trialUsers ?? 0) || 0,
                      expiredTrials: Number((serverSystemStats as any)?.expiredTrials ?? 0) || 0,
                      totalViews: Number((serverSystemStats as any)?.totalViews ?? totalViews) || Number(totalViews || 0),
                      totalShares: Number((serverSystemStats as any)?.totalShares ?? totalShares) || Number(totalShares || 0),
                      totalAdViews: Number((serverSystemStats as any)?.totalAdViews ?? 0) || 0,
                      totalAdClicks: Number((serverSystemStats as any)?.totalAdClicks ?? (allowMockStats ? adsList.reduce((s,a)=>s+(a.clicks||0),0) : 0)) || (allowMockStats ? adsList.reduce((s,a)=>s+(a.clicks||0),0) : 0),
                      totalAdRedirects: Number((serverSystemStats as any)?.totalAdRedirects ?? (allowMockStats ? adsList.reduce((s,a)=>s+(a.redirects||0),0) : 0)) || (allowMockStats ? adsList.reduce((s,a)=>s+(a.redirects||0),0) : 0),
                      totalCardClicks: Number((serverSystemStats as any)?.totalCardClicks ?? 0) || 0,
                      totalEmailClicks: Number((serverSystemStats as any)?.totalEmailClicks ?? 0) || 0,
                      totalShareClicks: Number((serverSystemStats as any)?.totalShareClicks ?? 0) || 0,
                      totalCallClicks: Number((serverSystemStats as any)?.totalCallClicks ?? 0) || 0,
                      totalCardSaveClicks: Number((serverSystemStats as any)?.totalCardSaveClicks ?? 0) || 0,
                      totalSocialClicks: Number((serverSystemStats as any)?.totalSocialClicks ?? 0) || 0,
                      totalQrScans: Number((serverSystemStats as any)?.totalQrScans ?? 0) || 0,
                      analyticsFlags: (serverSystemStats as any)?.analyticsFlags,
                      demographics: (serverSystemStats as any)?.demographics || { ageGroups: {}, genders: {}, locations: {} },
                    }}
                    showRevenue={false}
                  />
                </div>
                <div className="mt-6 bg-white border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">Subscriptions Overview</h3>
                      <p className="text-sm text-gray-500">Active vs Inactive subscribers</p>
                    </div>
                    <button onClick={async()=>{
                      try {
                        setLoadingSubs(true);
                        const [sum, exp] = await Promise.allSettled([getSubscriptionSummary(), listExpiredSubscriptions()]);
                        if (sum.status==='fulfilled') setSubSummary(sum.value);
                        if (exp.status==='fulfilled') setExpiredUsers(Array.isArray(exp.value)?exp.value:[]);
                        safeToast('success','Subscription stats refreshed');
                      } catch { safeToast('error','Failed to refresh'); } finally { setLoadingSubs(false); }
                    }} className="px-3 py-1 border rounded text-sm">Refresh</button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div className="p-3 rounded border bg-green-50">
                      <div className="text-green-700 text-sm">Active Subscribers</div>
                      <div className="text-2xl font-bold text-green-900">{subSummary.active}</div>
                    </div>
                    <div className="p-3 rounded border bg-red-50">
                      <div className="text-red-700 text-sm">Inactive Subscribers</div>
                      <div className="text-2xl font-bold text-red-900">{subSummary.inactive}</div>
                    </div>
                    <div className="p-3 rounded border bg-yellow-50">
                      <div className="text-yellow-800 text-sm">Gold</div>
                      <div className="text-2xl font-bold text-yellow-900">{subSummary.gold}</div>
                    </div>
                    <div className="p-3 rounded border bg-gray-50">
                      <div className="text-gray-700 text-sm">Silver</div>
                      <div className="text-2xl font-bold text-gray-900">{subSummary.silver}</div>
                    </div>
                  </div>
                  
                </div>
                <div className="mt-4 flex gap-2">
                  <button onClick={downloadBasicStatsPDF} className="px-3 py-2 border rounded">Download Basic</button>
                  <button onClick={downloadComprehensiveStatsPDF} className="px-3 py-2 border rounded">Download Comprehensive</button>
                </div>
              </div>
            )}

            {/* Activity log moved to System Admin Managers panel for Super Admin review */}

          </div>
        </div>
      </div>

  <CreateCardUserModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        submit={async (payload:any) => { return { user: payload }; }}
        onCreated={(resp:any) => {
          setUsers(prev => {
            const raw = (resp && (resp.user || resp)) || {};
            const email = String(raw.email || '').toLowerCase();
            let username = raw.username;
            if (!username || username === 'undefined') {
              const fromEmail = email ? email.split('@')[0] : '';
              const fromName = raw.name ? String(raw.name).toLowerCase().replace(/\s+/g, '') : '';
              username = fromEmail || fromName || `user${Math.random().toString(36).slice(2,6)}`;
            }
            let id = String(raw.id || raw._id || username);
            if (!id || id === 'undefined') id = `${username}_${Math.random().toString(36).slice(2,6)}`;
            const idx = prev.findIndex(p => (p.email && String(p.email).toLowerCase() === email) || p.username === username || p.id === id || p._id === id);
            const base = {
              ...raw,
              id,
              username,
              email,
              isActive: typeof raw.isActive === 'boolean' ? raw.isActive : true,
              templateId: raw.templateId || '1',
              templateMetaId: (raw as any).templateMetaId,
              analytics: raw.analytics || { views: 0, saves: 0, contactClicks: 0, messageClicks: 0, socialClicks: 0, qrScans: 0, shares: 0 },
              subscription: raw.subscription || { type: 'trial', expiresAt: new Date(Date.now() + 14*24*60*60*1000).toISOString() },
              createdAt: raw.createdAt || new Date().toISOString(),
              updatedAt: raw.updatedAt || new Date().toISOString(),
            };
            let next: any[];
            if (idx >= 0) {
              next = [...prev];
              next[idx] = { ...next[idx], ...base };
            } else {
              // ensure unique id in case of collisions
              const seen = new Set(prev.map(x => String(x.id)));
              let uniqueId = base.id;
              while (seen.has(uniqueId)) uniqueId = `${uniqueId}_${Math.random().toString(36).slice(2,4)}`;
              next = [{ ...base, id: uniqueId }, ...prev];
            }
            try { localStorage.setItem('users', JSON.stringify(next)); } catch {}
            return next;
          });
          safeToast('success','User created');
          recordActivity('Created new user card');
          setShowCreateModal(false);
        }}
      />

      {customizeUserId && (
  <CardCustomizationPage
    user={(users.find((u:any)=>u.id===customizeUserId) || currentUser || users[0])}
    ads={[]}
    onClose={() => setCustomizeUserId(null)}
    onSave={async (id:string, updates:any)=> {
      setUsers(prev => {
        const next = prev.map(u => u.id === id ? { ...u, ...updates } : u);
        try { localStorage.setItem('users', JSON.stringify(next)); } catch {}
        return next;
      });
      try {
        const key = id && /^[a-f\d]{24}$/i.test(id) ? id : (users.find(u=>u.id===id)?.username || id);
        const payload: any = {
          name: updates.name,
          position: updates.position,
          company: updates.company,
          email: updates.email,
          phone: updates.phone,
          avatar: updates.avatar,
          coverImage: updates.coverImage,
          bio: updates.bio,
          location: updates.location,
          colors: updates.colors,
          website: updates.website,
          socialLinks: updates.socialLinks,
          streamingLinks: updates.streamingLinks,
          // Enforce exclusive template persistence
          templateId: updates.templateMetaId ? null : updates.templateId,
          templateMetaId: updates.templateMetaId ?? null,
          subscription: updates.subscription,
          displayName: updates.displayName,
        };
        await updateUser(String(key), payload);
        // Immediately hydrate from server to ensure cross-panel sync
        try {
          const fresh = await getUser(String(key));
          setUsers(prev => prev.map(u => (u.id === id ? { ...u, ...fresh } : u)));
          try { localStorage.setItem('users', JSON.stringify(users)); } catch {}
        } catch {}
      } catch (e) {
        safeToast('error', 'Failed to save to server; kept local changes');
      }
      recordActivity('Customized user card');
      setCustomizeUserId(null);
    }}
  />
      )}

      <EditPlanModal open={planModalOpen} plan={editingPlan} onClose={() => setPlanModalOpen(false)} onSave={(p:any)=>{ handleSavePlan(p); setPlanModalOpen(false); }} />
      <EditAdModal open={adModalOpen} ad={editingAd} onClose={() => setAdModalOpen(false)} onSave={(a:any)=>{ handleSaveAd(a); setAdModalOpen(false); }} />
      <EditTemplateModal open={tplModalOpen} tpl={editingTpl} onClose={() => setTplModalOpen(false)} onSave={(t:any)=>{ handleSaveTemplate(t); setTplModalOpen(false); }} />

    </div>
  );
}

function BulkSMSForm({ leads, onSubmit, preset }: { leads: Lead[]; onSubmit: (payload:any)=>void; preset?: { recipients?: string[]; title?: string; message?: string; group?: 'all'|'users'|'non_users' } }) {
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [title, setTitle] = useState(preset?.title || '');
  const [message, setMessage] = useState(preset?.message || '');
  const [group, setGroup] = useState<'all'|'users'|'non_users'>(preset?.group || 'all');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<any[]>(() => { try { return JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]'); } catch { return []; } });
  // Compact leads list enhancements
  const [leadSearch, setLeadSearch] = useState('');
  const [page, setPage] = useState(1);
  const filteredLeads = useMemo(() => {
    let base: any[] = [];
    if (group === 'all') base = leads as any[];
    else if (group === 'users') base = leads.filter((l:any) => l.isExistingUser || l.existingUserId) as any[];
    else base = leads.filter((l:any) => !(l.isExistingUser || l.existingUserId)) as any[];
    const q = leadSearch.trim().toLowerCase();
    if (q) {
      base = base.filter((l:any) => {
        const name = String(l.fullName || l.name || '').toLowerCase();
        const phone = String(l.phoneNumber || '').toLowerCase();
        const email = String(l.email || '').toLowerCase();
        return name.includes(q) || phone.includes(q) || email.includes(q);
      });
    }
    return base;
  }, [leads, group, leadSearch]);
  const itemsPerPage = 30;
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / itemsPerPage));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const displayedLeads = useMemo(() => {
    const start = (pageSafe - 1) * itemsPerPage;
    return filteredLeads.slice(start, start + itemsPerPage);
  }, [filteredLeads, pageSafe]);
  const recipientsFromSelection = filteredLeads.filter(l => selectedLeads.includes(getId(l))).map((l:any) => l.phoneNumber).filter(Boolean);
  const recipients = Array.from(new Set([...(preset?.recipients || []), ...recipientsFromSelection]));

  const submit = () => {
    if (!title.trim()) return safeToast('error', 'Title is required');
    if (!message.trim()) return safeToast('error', 'Message is required');
    if (recipients.length === 0) return safeToast('error', 'Select at least one lead with phone');
    const payload = { type: 'sms', title, message, recipients };
    onSubmit(payload);
    // local notification
    const note = { id: `n_${Date.now()}`, text: 'Your bulk SMS is pending System Admin approval', at: new Date().toISOString() };
    const nextNotes = [note, ...notifications];
    setNotifications(nextNotes);
    try { localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(nextNotes)); } catch {}
    setTitle(''); setMessage(''); setSelectedLeads([]);
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-semibold">Compose Bulk SMS</h3>
        <div className="relative">
          <button onClick={() => setShowNotifications(s => !s)} className="px-3 py-1 bg-yellow-100 border rounded flex items-center gap-2">
            <svg className="w-4 h-4 text-yellow-700" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.686 2 6 4.686 6 8v4l-2 2v1h16v-1l-2-2V8c0-3.314-2.686-6-6-6z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-sm">Notifications</span>
            {notifications.length > 0 && <span className="ml-1 inline-block w-3 h-3 bg-red-600 rounded-full"></span>}
          </button>
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-[calc(100vw-3rem)] sm:w-64 max-w-xs bg-white border rounded shadow p-2 z-40">
              {notifications.length === 0 ? <div className="text-sm text-gray-500 p-2">No notifications</div> : (
                <ul className="space-y-1 max-h-48 overflow-auto">
                  {notifications.map(n => (
                    <li key={n.id} className="text-sm text-gray-700 border-b last:border-b-0 pb-1">{n.text} <div className="text-xs text-gray-400">{new Date(n.at).toLocaleString()}</div></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-center gap-3 mb-2">
          <label className="text-sm font-medium">Group</label>
          <select value={group} onChange={(e)=> setGroup(e.target.value as any)} className="px-2 py-1 border rounded text-sm">
            <option value="all">All leads</option>
            <option value="users">Existing users</option>
            <option value="non_users">Non-users</option>
          </select>
        </div>
        <label className="block text-sm font-medium mb-1">Select Leads</label>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={leadSearch}
              onChange={(e)=> { setLeadSearch(e.target.value); setPage(1); }}
              placeholder="Search name, phone, email"
              className="flex-1 min-w-0 sm:min-w-[200px] px-2 py-1 border rounded text-sm"
            />
            <span className="text-xs text-gray-600">{filteredLeads.length} match{filteredLeads.length!==1?'es':''}</span>
            {filteredLeads.length > itemsPerPage && (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-gray-600">Page {pageSafe} of {totalPages}</span>
                <button onClick={()=> setPage(p=> Math.max(1,p-1))} className="text-xs px-2 py-1 border rounded disabled:opacity-50" disabled={pageSafe===1}>Prev</button>
                <button onClick={()=> setPage(p=> Math.min(totalPages,p+1))} className="text-xs px-2 py-1 border rounded disabled:opacity-50" disabled={pageSafe===totalPages}>Next</button>
              </div>
            )}
          </div>
          <div className="border rounded bg-gray-50 divide-y">
            {displayedLeads.map((l:any) => (
              <div key={l.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-2 px-2 py-1 text-xs">
                <input
                  type="checkbox"
                  className="scale-90"
                  checked={selectedLeads.includes(l.id)}
                  onChange={(e)=> setSelectedLeads(s => e.target.checked ? [...s, l.id] : s.filter(x=>x!==l.id))}
                />
                <div className="truncate">
                  <span className="font-medium mr-1">{l.fullName || l.name || 'Unnamed'}</span>
                  <span className="text-gray-500">{l.phoneNumber || '(no phone)'}</span>
                </div>
                {l.email && <span className="hidden md:inline text-gray-400 truncate max-w-[140px]">{l.email}</span>}
              </div>
            ))}
            {displayedLeads.length === 0 && <div className="px-2 py-2 text-xs text-gray-500">No leads found</div>}
          </div>
        </div>
        {preset?.recipients && preset.recipients.length > 0 && (
          <div className="mt-2 text-xs text-gray-600">Preset recipients included: {preset.recipients.length}</div>
        )}
        <div className="mt-2 flex justify-end">
          {/* Select only leads that have phone numbers for SMS selection; use set-based equality to avoid duplicates issues */}
          <button
            onClick={() => {
              const selectable = filteredLeads.filter((x:any) => x.phoneNumber).map((x:any) => x.id);
              const selSet = new Set(selectedLeads);
              const allSelected = selectable.length > 0 && selectable.every(id => selSet.has(id));
              if (allSelected) setSelectedLeads([]);
              else setSelectedLeads(selectable);
            }}
            className="text-sm text-blue-600 hover:underline"
          >
            {(() => { const selectable = filteredLeads.filter((x:any)=>x.phoneNumber).map((x:any)=>x.id); const selSet = new Set(selectedLeads); const allSelected = selectable.length>0 && selectable.every(id=>selSet.has(id)); return allSelected ? 'Deselect all' : 'Select all'; })()}
          </button>
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-sm font-medium mb-1">Title</label>
        <input value={title} onChange={(e)=> setTitle(e.target.value)} className="w-full p-2 border rounded" placeholder="Message title (will appear in admin review)" />
      </div>

      <div className="mb-3">
        <label className="block text-sm font-medium mb-1">Message</label>
        <textarea value={message} onChange={(e)=> setMessage(e.target.value)} className="w-full p-2 border rounded h-28" placeholder="Write your message here" />
      </div>

      <div className="flex gap-2">
        <button onClick={submit} className="px-4 py-2 bg-blue-600 text-white rounded">Queue for Review</button>
        <button onClick={() => { setSelectedLeads([]); setMessage(''); setTitle(''); }} className="px-4 py-2 border rounded">Reset</button>
      </div>
      <div className="mt-3 text-xs text-gray-500">Your message will be reviewed by System Admin before sending. Rejections will appear in Notifications.</div>
    </div>
  );
}

function BulkEmailForm({ leads, onSubmit, preset }: { leads: Lead[]; onSubmit: (payload:any)=>void; preset?: { recipients?: string[]; subject?: string; message?: string; group?: 'all'|'users'|'non_users' } }) {
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [subject, setSubject] = useState(preset?.subject || '');
  const [message, setMessage] = useState(preset?.message || '');
  const [group, setGroup] = useState<'all'|'users'|'non_users'>(preset?.group || 'all');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<any[]>(() => { try { return JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]'); } catch { return []; } });
  const [leadSearch, setLeadSearch] = useState('');
  const [page, setPage] = useState(1);
  const filteredLeads = useMemo(() => {
    let base: any[] = [];
    if (group === 'all') base = leads as any[];
    else if (group === 'users') base = leads.filter((l:any) => l.isExistingUser || l.existingUserId) as any[];
    else base = leads.filter((l:any) => !(l.isExistingUser || l.existingUserId)) as any[];
    const q = leadSearch.trim().toLowerCase();
    if (q) {
      base = base.filter((l:any) => {
        const name = String(l.fullName || l.name || '').toLowerCase();
        const phone = String(l.phoneNumber || '').toLowerCase();
        const email = String(l.email || '').toLowerCase();
        return name.includes(q) || phone.includes(q) || email.includes(q);
      });
    }
    return base;
  }, [leads, group, leadSearch]);
  const itemsPerPage = 30;
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / itemsPerPage));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const displayedLeads = useMemo(() => {
    const start = (pageSafe - 1) * itemsPerPage;
    return filteredLeads.slice(start, start + itemsPerPage);
  }, [filteredLeads, pageSafe]);
  const recipientsFromSelection = filteredLeads.filter(l => selectedLeads.includes(getId(l))).map((l:any) => l.email).filter(Boolean);
  const recipients: string[] = Array.from(new Set<string>([...(preset?.recipients || []), ...recipientsFromSelection] as string[]));

  const submitEmail = () => {
    if (!subject.trim()) return safeToast('error', 'Subject is required');
    if (!message.trim()) return safeToast('error', 'Message is required');
    if (recipients.length === 0) return safeToast('error', 'Select at least one lead with email');
    const payload = { type: 'email', subject, message, recipients };
    onSubmit(payload);
    const note = { id: `n_${Date.now()}`, text: 'Your bulk Email is pending System Admin approval', at: new Date().toISOString() };
    const nextNotes = [note, ...notifications];
    setNotifications(nextNotes);
    try { localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(nextNotes)); } catch {}
    setSubject(''); setMessage(''); setSelectedLeads([]);
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-semibold">Compose Bulk Email</h3>
        <div className="relative">
          <button onClick={() => setShowNotifications(s => !s)} className="px-3 py-1 bg-yellow-100 border rounded flex items-center gap-2">
            <svg className="w-4 h-4 text-yellow-700" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.686 2 6 4.686 6 8v4l-2 2v1h16v-1l-2-2V8c0-3.314-2.686-6-6-6z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-sm">Notifications</span>
            {notifications.length > 0 && <span className="ml-1 inline-block w-3 h-3 bg-red-600 rounded-full"></span>}
          </button>
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-[calc(100vw-3rem)] sm:w-64 max-w-xs bg-white border rounded shadow p-2 z-40">
              {notifications.length === 0 ? <div className="text-sm text-gray-500 p-2">No notifications</div> : (
                <ul className="space-y-1 max-h-48 overflow-auto">
                  {notifications.map(n => (
                    <li key={n.id} className="text-sm text-gray-700 border-b last:border-b-0 pb-1">{n.text} <div className="text-xs text-gray-400">{new Date(n.at).toLocaleString()}</div></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-center gap-3 mb-2">
          <label className="text-sm font-medium">Group</label>
          <select value={group} onChange={(e)=> setGroup(e.target.value as any)} className="px-2 py-1 border rounded text-sm">
            <option value="all">All leads</option>
            <option value="users">Existing users</option>
            <option value="non_users">Non-users</option>
          </select>
        </div>
        <label className="block text-sm font-medium mb-1">Select Leads</label>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={leadSearch}
              onChange={(e)=> { setLeadSearch(e.target.value); setPage(1); }}
              placeholder="Search name, phone, email"
              className="flex-1 min-w-0 sm:min-w-[200px] px-2 py-1 border rounded text-sm"
            />
            <span className="text-xs text-gray-600">{filteredLeads.length} match{filteredLeads.length!==1?'es':''}</span>
            {filteredLeads.length > itemsPerPage && (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-gray-600">Page {pageSafe} of {totalPages}</span>
                <button onClick={()=> setPage(p=> Math.max(1,p-1))} className="text-xs px-2 py-1 border rounded disabled:opacity-50" disabled={pageSafe===1}>Prev</button>
                <button onClick={()=> setPage(p=> Math.min(totalPages,p+1))} className="text-xs px-2 py-1 border rounded disabled:opacity-50" disabled={pageSafe===totalPages}>Next</button>
              </div>
            )}
          </div>
          <div className="border rounded bg-gray-50 divide-y">
            {displayedLeads.map((l:any) => (
              <div key={l.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-2 px-2 py-1 text-xs">
                <input
                  type="checkbox"
                  className="scale-90"
                  checked={selectedLeads.includes(l.id)}
                  onChange={(e)=> setSelectedLeads(s => e.target.checked ? [...s, l.id] : s.filter(x=>x!==l.id))}
                />
                <div className="truncate">
                  <span className="font-medium mr-1">{l.fullName || l.name || 'Unnamed'}</span>
                  <span className="text-gray-500">{l.email || '(no email)'}</span>
                </div>
                {l.phoneNumber && <span className="hidden md:inline text-gray-400 truncate max-w-[140px]">{l.phoneNumber}</span>}
              </div>
            ))}
            {displayedLeads.length === 0 && <div className="px-2 py-2 text-xs text-gray-500">No leads found</div>}
          </div>
        </div>
        {preset?.recipients && preset.recipients.length > 0 && (
          <div className="mt-2 text-xs text-gray-600">Preset recipients included: {preset.recipients.length}</div>
        )}
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => {
              const selectable = filteredLeads.filter((x:any) => x.email).map((x:any) => x.id);
              const selSet = new Set(selectedLeads);
              const allSelected = selectable.length > 0 && selectable.every(id => selSet.has(id));
              if (allSelected) setSelectedLeads([]);
              else setSelectedLeads(selectable);
            }}
            className="text-sm text-blue-600 hover:underline"
          >{(() => { const selectable = filteredLeads.filter((x:any)=>x.email).map((x:any)=>x.id); const selSet = new Set(selectedLeads); const allSelected = selectable.length>0 && selectable.every(id=>selSet.has(id)); return allSelected ? 'Deselect all' : 'Select all'; })()}
          </button>
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-sm font-medium mb-1">Subject</label>
        <input value={subject} onChange={(e)=> setSubject(e.target.value)} className="w-full p-2 border rounded" placeholder="Message subject (will appear in admin review)" />
      </div>

      <div className="mb-3">
        <label className="block text-sm font-medium mb-1">Message</label>
        <textarea value={message} onChange={(e)=> setMessage(e.target.value)} className="w-full p-2 border rounded h-28" placeholder="Write your message here" />
      </div>

      <div className="flex gap-2">
        <button onClick={submitEmail} className="px-4 py-2 bg-amber-500 text-white rounded">Queue for Review</button>
        <button onClick={()=> { setSelectedLeads([]); setSubject(''); setMessage(''); }} className="px-4 py-2 border rounded">Reset</button>
      </div>
      <div className="mt-3 text-xs text-gray-500">Your message will be reviewed by System Admin before sending. Rejections will appear in Notifications.</div>
    </div>
  );
}
